import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * Deck Selector UI Module
 * Handles the game start deck selection screen with a 2-stage flow:
 * Stage 1: Character Selection (Grid)
 * Stage 2: Deck Selection (List for chosen character)
 */


import { Cards } from './cards.js';
import { Firebase } from '../firebase-loader.js';
import { MangaGamePreference } from './manga-game-preference.js';
import { LucidGamePreference } from './lucid-game-preference.js';
import { Platform } from './platform.js';
import { phoneDeckPageSize, PHONE_PREFERENCES_ROW_PX } from './deck-page-fit.js';
import { SummonerRoster } from './summoner-roster.js';
import { LoadScheduler } from '../utils/load-scheduler.js';
import { LobbyCover } from './lobby-cover.js';
import { imageAttemptDeadlineMs } from './image-attempt-deadline.js';
let DeckSelector = {};

DeckSelector = (function () {
    const viewportHandlerKey = Symbol.for('moonfall.deckSelector.viewportChangeHandler');
    const previousViewportHandler = window[viewportHandlerKey];
    if (typeof previousViewportHandler === 'function') {
        window.removeEventListener('moonfall:viewport-change', previousViewportHandler);
    }
    const portraitRetryRuntimeKey = Symbol.for('moonfall.deckSelector.portraitRetryRuntime');
    window[portraitRetryRuntimeKey]?.dispose?.();
    const CHARACTER_PAGE_SIZE = 4;
    /* A PORTRAIT ATTEMPT GETS THE IMAGE ATTEMPT LADDER (image-attempt-deadline.js:
       30 s, then 60 s past the cache). It was 12 s and 12 s: on 1 Mbps, beside
       the Collection's page, the 521 KB and 835 KB portraits were aborted at
       12.5 s, asked for again past the cache, aborted again at 24.5 s — every
       byte thrown away twice (28 September 2026, the Collection's slow-link
       spec), the defect card art had until 16 September. The deadline is for a
       request that never answers; a slow one is left to finish. */
    const CHARACTER_PORTRAIT_ATTEMPTS = 2;
    const CHARACTER_PORTRAIT_FETCH_CONCURRENCY = 2;
    const CHARACTER_PORTRAIT_LOCAL_LOAD_TIMEOUT_MS = 2500;
    const CHARACTER_PORTRAIT_DECODE_TIMEOUT_MS = 500;
    const CHARACTER_PORTRAIT_BLOB_CACHE_MAX_ENTRIES = 32;
    const CHARACTER_PORTRAIT_BLOB_CACHE_MAX_BYTES = 64 * 1024 * 1024;
    /* A portrait the warm could not get is not waited on for this long: the
       page is presented with that card's neutral, retrying well instead. */
    const PORTRAIT_FAILURE_GRACE_MS = 60000;
    /* THE ROSTER IS PAGED HERE (summoner-roster.js). The account's summoners
       are read whole, up to the route's cap for one read, and every page
       they cover is cut from that one answer: its cursor is this marker, not
       a route cursor. A page past the roster is read from the route's own
       cursor, one page ahead, as before. */
    const ROSTER_PAGE = 'roster';
    /* The pages the lobby decodes ahead: the first and the one a Next is
       likeliest to reach. Every other roster portrait is fetched as bytes
       only (warmRosterAhead): decoded pixels are the heavy kind of memory. */
    const DECODED_AHEAD_PAGES = 2;
    /* A deck list read longer ago than this is still presented at once, and
       read again behind the view while its summoner is on screen: a deck
       made, changed or deleted on this device drops every list at once
       (`moonfall:decks-changed`), so only another device can make one stale. */
    const DECK_FRESH_MS = 60000;
    /* Decoded portraits kept as the very <img> that decoded them, so Back, a
       reopen and a page turn adopt them: two pages' worth (the one on screen
       and the one a turn is likeliest to reach). Bounded because decoded
       pixels are the heavy kind of memory on a phone; the encoded bytes stay
       in the portrait blob cache, so an evicted page decodes again quickly. */
    const DECODED_PORTRAIT_MAX_ENTRIES = 8;
    /* The one loading state's hard cap, counted from the moment the page's
       data is in: past it the page is presented with whatever has decoded,
       and a straggler shows the moment it settles. It was the mulligan's
       1500 ms, but the mulligan spends a server clock and this does not:
       measured 25 September 2026 on the WebKit phone seat (4G), four ~835 KB
       summoner portraits need ~2.2 s, so the grid showed and its portraits
       landed 2-3 s later. The press stays answered (busy) meanwhile, and the
       page still appears the moment its portraits are in. */
    const STAGE_REVEAL_CAP_MS = 3500;
    const DECK_WARM_CONCURRENCY = 2;
    /* A press on a summoner whose decks are still being read waits on the
       card itself; this caps that wait, after which the deck stage opens with
       its truthful "could not load" state and a retry. */
    const DECK_READ_TIMEOUT_MS = 12000;
    const DECK_PAGE_SIZE = 25;
    /* THE PAGE IS MEASURED, NOT ASSUMED. A landscape phone hands the deck list
       one or two rows of room, and the list clips, so a fixed page of three
       drew the rest outside its own box. `phoneDeckPageSize` reads the room
       the stage actually has; the pager walks whatever is left over. */
    function phoneDeckChoicePageSize() {
        const content = document.getElementById('ds-content-area');
        if (!content) return 3;
        /* THE SWITCH ROW IS THE SUMMONER STAGE'S NOW, so the deck stage gets
           its full height back and pages more decks — the owner's proposal:
           "when you select your deck, it's just deck. Nothing else."

           Derived from the stage rather than assumed, so that if the row is
           ever put back here the page size follows it instead of silently
           over-filling. `dataset.stage` is set before this runs, and before the
           body is replaced, so it is already the stage being rendered.

           When the row IS displayed it must never be measured below the height
           it is published at: a live read while the row is detached (an error
           stage wipes the whole area; the observer re-attaches it) honestly
           returns 0, which bought the page one extra deck and put it straight
           back outside the list. */
        const showsPreferences = content.dataset.stage !== 'decks';
        const measured = battlePreferencesEl
            ? battlePreferencesEl.getBoundingClientRect().height
            : 0;
        return phoneDeckPageSize({
            contentHeightPx: content.clientHeight,
            preferencesHeightPx: showsPreferences
                ? Math.max(measured, PHONE_PREFERENCES_ROW_PX)
                : 0,
        });
    }
    let isOpen = false;
    const SURFACE_OWNER = 'deck-selector';
    let sessionGeneration = 0;

    /* Same carve-out as feedback.js, for the same reason and on the same
       marker. `game-active` is still on the body while the terminal result
       popup is up, and that popup's PRIMARY call to action — "Play Again" —
       runs straight through here: chooseRematchLoadout() awaits
       DeckSelector.choose(), which resolves `{kind:'blocked'}` the moment this
       returns false, and game-end.js answers any non-'selected' kind by
       quietly re-enabling the button with no status and no error. So the
       headline button on the results screen did nothing at all (so did the
       since-retired Share feedback button beside it). The 10-second
       opponent-ready auto-advance died the same way.

       `game-result-active` is mounted only while a terminal modal is, so every
       mid-match refusal is untouched. */
    function claimLobbySurface() {
        const body = document.body;
        if (body?.classList.contains('game-transition-pending')) return false;
        if (body?.classList.contains('game-active')
            && !body.classList.contains('game-result-active')) return false;
        const owner = document.body?.dataset?.lobbySurfaceOwner;
        if (owner && owner !== SURFACE_OWNER) return false;
        if (document.body) document.body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
        return true;
    }

    function releaseLobbySurface() {
        if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
            delete document.body.dataset.lobbySurfaceOwner;
        }
    }
    let sessionAbortController = null;
    let activeChoice = null;
    let mangaPreferenceControl = null;
    let lucidPreferenceControl = null;
    let returnFocusTo = null;
    let excludedCharacter = null;
    let selectedCharacter = null; // Track currently selected character for Stage 2
    let characterPage = 1;
    let characterPageIsDone = true;
    let characterPageCursors = new Map([[1, null]]);
    let deckPage = 1;
    let deckPageIsDone = true;
    let deckPageCursors = new Map([[1, null]]);
    let cachedDecks = [];
    let deckLocalSlice = 0;
    let deckLogicalPage = 1;
    let charactersData = [];
    let characterPortraitByKey = new Map();
    let portraitRenderGeneration = 0;
    let portraitRenderAbortController = null;
    const activePortraitObjectUrls = new Set();
    const portraitBlobCache = new Map();
    const portraitRetryRecords = new Map();
    const identityPortraitRecords = new Map();
    let portraitBlobCacheBytes = 0;

    /* ---- THE SELECTOR IS FINISHED BEFORE THE PRESS -------------------------

       The owner, 2026-09-24: the game "isn't snappy at all" — screens morph
       while they load, loading is chaotic, and "too much is fetched just in
       time instead of ahead of time". Measured here on the real backend at
       110 ms RTT: Start Adventure painted nothing for ~620 ms, then "Loading
       characters…", then 41 elements in four waves, then the portraits one at
       a time; a press on a summoner wiped the grid to a spinner for ~490 ms.

       So the selector is WARMED once the lobby is up and again on intent
       (lobby.js): the account's summoners whole (summoner-roster.js), the
       first two pages' portraits fetched AND decoded, every other summoner's
       portrait fetched, and every summoner's decks read — small sets, read
       whole (owner, 28 September 2026: "the deck names can be paired in
       totality ... it's just text", "you could load like at least 20"
       summoners). A decoded portrait is kept as the very <img> that decoded
       it and ADOPTED into the grid, so the dialog's first frame paints
       finished pixels instead of starting a second decode. Every portrait a
       session decodes joins the same store, so Back and a reopen adopt
       theirs too. */
    const decodedPortraits = new Map();
    const portraitBlobWarms = new Map();
    const portraitWarms = new Map();
    const portraitWarmFailures = new Map();
    let deckReads = new Map();
    let preparedPages = new Map();
    let pageTurn = null;
    let openingChoice = null;
    let pendingFocusCharacterId = null;

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        /* The FALLBACK is interpolated too. It is returned verbatim whenever
           i18n has not resolved — the normal case on a cold load — and the
           ui.json wording these fallbacks mirror carries `{{name}}`
           placeholders, so a user could be shown a literal `{{count}}`. Both
           brace styles are handled because the copy uses both. */
        const source = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements || {}).reduce(
            (text, [name, value]) => String(text)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(source == null ? key : source),
        );
    }

    function htmlText(key, fallback, replacements = {}) {
        return escapeHtml(uiText(key, fallback, replacements));
    }

    function deckNameHtml(deck) {
        if (deck?.isOfficialStarter && deck.templateId) {
            return htmlText(`deckSelector.starterDeckNames.${deck.templateId}`, deck.name || deck.templateId);
        }
        return escapeHtml(deck?.name || '');
    }

    /* The dialog's own focus placements (the × at open, a champion card) are
       told apart from the player's: a focus the player moved inside the dialog
       is theirs to keep (focusCharacterChoiceUnlessMoved). */
    let placingFocus = false;
    let playerFocus = null;

    function noteFocusIn(event) {
        if (!placingFocus) playerFocus = event.target;
    }

    function focusWithoutScroll(element) {
        if (!element?.isConnected || typeof element.focus !== 'function') return false;
        placingFocus = true;
        try {
            element.focus({ preventScroll: true });
        } catch (_) {
            element.focus();
        } finally {
            placingFocus = false;
        }
        return document.activeElement === element;
    }

    function getModalFocusableElements(modal) {
        if (!modal) return [];
        return Array.from(modal.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => (
            element.getAttribute('aria-hidden') !== 'true'
            // A held stage is built but not painted, so none of it can hold focus.
            && !element.closest('.ds-content-area[data-reveal="held"]')
        ));
    }

    function focusCharacterChoice(preferredCharacterId = null) {
        const escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(String(preferredCharacterId || ''))
            : String(preferredCharacterId || '').replace(/["\\]/g, '\\$&');
        const preferred = preferredCharacterId
            ? document.querySelector(`.ds-character-card[data-char-id="${escapedId}"]:not(:disabled)`)
            : null;
        return focusWithoutScroll(
            preferred || document.querySelector('.ds-character-card:not(:disabled)')
        );
    }

    /* Only a focus that has fallen out of the dialog is brought back. The
       grid is interactive before its art lands (docs/asset-loading.md law
       2), and a page held until its portraits settle is drawn later still, so
       by then a player may be on a switch, the pager or the × — and taking the
       keyboard back to a champion card lost their place. A slow decode
       (WebKit, a phone on a slow link) made that the usual order, not a race. */
    function focusCharacterChoiceUnlessMoved(preferredCharacterId) {
        const modal = document.getElementById('deck-selector-modal');
        const active = document.activeElement;
        const playerMovedHere = active === playerFocus && !!modal?.contains(active);
        if (!modal || active === modal || !modal.contains(active) || !playerMovedHere) {
            focusCharacterChoice(preferredCharacterId);
        }
    }

    function isCurrentSession(generation) {
        return isOpen && generation === sessionGeneration;
    }

    /**
     * Revoke every portrait blob this session created.
     *
     * Order matters: an <img> still pointing at a revoked object URL is blank
     * FOREVER — `naturalWidth` stays 0 and the browser will not re-fetch a
     * revoked blob — so each element is taken off its URL, and put back on the
     * source it came from, before anything is revoked. (The singular
     * releasePortraitObjectUrl(image) always did the element half; this one
     * skipping it is what left a re-rendered starter-deck portrait blank.)
     */
    function releasePortraitObjectUrls() {
        document.querySelectorAll('img[data-portrait-object-url]').forEach((image) => {
            if (!activePortraitObjectUrls.has(image.dataset.portraitObjectUrl)) return;
            const originalSrc = image.dataset.originalSrc;
            if (originalSrc) {
                image.setAttribute('src', originalSrc);
            } else {
                image.removeAttribute('src');
            }
            delete image.dataset.portraitObjectUrl;
            delete image.dataset.portraitSource;
        });
        activePortraitObjectUrls.forEach((url) => {
            try {
                URL.revokeObjectURL(url);
            } catch (_) {
                // A revoked or browser-owned URL needs no further cleanup.
            }
        });
        activePortraitObjectUrls.clear();
    }

    function clearPortraitRender() {
        portraitRenderAbortController?.abort();
        portraitRenderAbortController = null;
        portraitRetryRecords.forEach((record, image) => {
            if (record.timerId !== null) window.clearTimeout(record.timerId);
            record.signal?.removeEventListener('abort', record.abortHandler);
            Cards?.disposeCardArtImage?.(image);
            if (image.classList.contains('ds-char-image-retrying')) {
                image.removeAttribute('src');
            }
        });
        portraitRetryRecords.clear();
        identityPortraitRecords.forEach((record, image) => {
            if (record.timerId !== null) window.clearTimeout(record.timerId);
            record.signal?.removeEventListener('abort', record.abortHandler);
            Cards?.disposeCardArtImage?.(image);
            image.removeAttribute('src');
        });
        identityPortraitRecords.clear();
        releasePortraitObjectUrls();
    }

    window[portraitRetryRuntimeKey] = {
        dispose() {
            clearPortraitRender();
            decodedPortraits.forEach(releaseDecodedPortrait);
            decodedPortraits.clear();
        },
    };

    function resetSessionData() {
        mangaPreferenceControl?.destroy?.();
        mangaPreferenceControl = null;
        lucidPreferenceControl?.destroy?.();
        lucidPreferenceControl = null;
        portraitRenderGeneration += 1;
        clearPortraitRender();
        selectedCharacter = null;
        pendingFocusCharacterId = null;
        openingChoice = null;
        // The deck lists outlive the dialog (deckReadKey): a reopen and every
        // summoner the lobby warmed open their decks at once.
        resetDeckPaginationState();
        resetCharacterPaginationState();
    }

    function beginSession(options = {}) {
        if (isOpen) {
            close({ restoreFocus: false });
        }
        if (!claimLobbySurface()) return null;

        sessionGeneration += 1;
        const generation = sessionGeneration;
        sessionAbortController = typeof AbortController === 'function'
            ? new AbortController()
            : null;
        excludedCharacter = options.excludedCharacter || null;
        returnFocusTo = options.returnFocusTo?.isConnected
            ? options.returnFocusTo
            : (document.activeElement instanceof HTMLElement && document.activeElement !== document.body
                ? document.activeElement
                : null);
        resetSessionData();
        isOpen = true;

        try {
            createModalIfNotExists();
            /* The body starts HELD: its one loading state, which shows itself
               only after the shared grace. A warmed page replaces it within
               this same task, so it never paints; otherwise the stage is built
               behind it and presented whole (loadSession). */
            const content = document.getElementById('ds-content-area');
            if (content) {
                content.replaceChildren();
                holdStage(content);
            }
            /* SYNCHRONOUSLY, before mounting. MutationObserver callbacks are
               microtasks, so on a re-open the row can still be detached from a
               previous stage render when `mount()` looks for its slot — and
               mounting then throws "preference slot is required". The observer
               covers later renders; this covers the one that races. */
            attachBattlePreferences();
            /* A status still being read joins the stage's hold instead
               (settleBattleStatuses): the switches are never drawn as
               "checking" and then redrawn a moment later. */
            if (!isPendingStatus(options.lucidStatus)) mountLucidPreference(options.lucidStatus);
            if (!isPendingStatus(options.mangaStatus)) mountMangaPreference(options.mangaStatus);
            const modal = document.getElementById('deck-selector-modal');
            if (modal) {
                modal.style.display = 'flex';
            }
            if (modal && !modal.dataset.focusWatch) {
                modal.dataset.focusWatch = 'true';
                modal.addEventListener('focusin', noteFocusIn);
            }
            playerFocus = null;
            focusWithoutScroll(document.getElementById('deck-selector-close'));
            return generation;
        } catch (error) {
            isOpen = false;
            sessionGeneration += 1;
            sessionAbortController?.abort();
            sessionAbortController = null;
            excludedCharacter = null;
            returnFocusTo = null;
            resetSessionData();
            releaseLobbySurface();
            throw error;
        }
    }

    /**
     * A warmed first page is presented in the task that opened the dialog:
     * the press's own frame is the finished screen. Anything not ready —
     * the page, a portrait, a battle status — sends the session down the held
     * path instead, which builds the stage behind the one loading state and
     * presents it whole.
     */
    async function loadSession(generation, options = {}) {
        /* A roster kept past its fresh window is presented and read again
           behind the view; a changed answer reconciles (onRosterChanged). */
        void SummonerRoster.revalidate();
        if (presentWarmFirstPage(generation, options)) return true;
        const statusesSettled = settleBattleStatuses(generation, options);
        await loadCharactersPage(1, { force: true, generation });
        if (!isCurrentSession(generation)) return false;
        /* The switches are part of the stage's layout, so the stage is built
           once they are mounted: nothing behind the hold moves while it waits.
           Not answered within the cap, they are drawn as unknown now and
           redrawn when they land. */
        if (isPendingStatus(options.mangaStatus) || isPendingStatus(options.lucidStatus)) {
            await settleWithinCap(statusesSettled, STAGE_REVEAL_CAP_MS);
            if (!isCurrentSession(generation)) return false;
            if (!lucidPreferenceControl) mountLucidPreference(undefined);
            if (!mangaPreferenceControl) mountMangaPreference(undefined);
        }
        const portraitsSettled = renderCharacterSelection(generation);
        if (charactersData.length === 0) {
            presentStage(generation);
            return true;
        }
        presentWhenSettled(generation, portraitsSettled);
        return true;
    }

    /* The roster the lobby read, for this account and language, whatever its
       age: one kept past its fresh window is read again behind the view
       (loadSession) rather than in front of the player. */
    function presentWarmFirstPage(generation, options) {
        const roster = SummonerRoster.peek();
        const page = roster ? rosterPage(roster, 1) : null;
        if (!page) return false;
        if (isPendingStatus(options.mangaStatus) || isPendingStatus(options.lucidStatus)) return false;
        if (!pagePortraitsReady(page)) return false;
        resetCharacterPaginationState();
        applyCharacterPage(1, page);
        renderCharacterSelection(generation);
        presentStage(generation);
        return true;
    }

    /* ---- the one loading state ---------------------------------------------

       A body that is not ready is HELD: the stage is laid out behind it at its
       final geometry (so its portraits decode at the size they will paint) but
       not painted (deck-selector.css), and one loading state covers the body.
       That state earns its right to appear only after the shared art grace, so
       a quick load never flashes it. When every portrait has settled and the
       battle statuses are in — or at the cap — the whole stage is presented in
       one step. Never a spinner and then pieces. */
    function holdStage(content) {
        content.dataset.reveal = 'held';
        content.setAttribute('aria-busy', 'true');
        if (content.querySelector(':scope > .ds-loading')) return;
        const loading = document.createElement('div');
        loading.className = 'ds-loading loading-spinner';
        loading.setAttribute('role', 'status');
        const ring = document.createElement('span');
        ring.className = 'ds-loading-ring';
        ring.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.className = 'ds-loading-label';
        label.textContent = uiText('deckSelector.loadingCharacters', 'Loading characters…');
        loading.append(ring, label);
        content.appendChild(loading);
    }

    function presentStage(generation) {
        if (!isCurrentSession(generation)) return false;
        const content = document.getElementById('ds-content-area');
        if (!content || content.dataset.reveal !== 'held') return false;
        delete content.dataset.reveal;
        content.removeAttribute('aria-busy');
        content.querySelector(':scope > .ds-loading')?.remove();
        /* A held grid could not take focus (it was not painted), so the
           keyboard waited on Close. Move it to the champions now — unless the
           player already moved it somewhere in the dialog themselves. */
        focusCharacterChoiceUnlessMoved(pendingFocusCharacterId);
        return true;
    }

    function presentWhenSettled(generation, portraitsSettled) {
        void settleWithinCap(portraitsSettled, STAGE_REVEAL_CAP_MS).then(() => presentStage(generation));
    }

    /* ---- battle statuses -------------------------------------------------- */

    function isPendingStatus(status) {
        return Boolean(status) && typeof status.then === 'function';
    }

    function mountLucidPreference(status) {
        attachBattlePreferences();
        lucidPreferenceControl?.destroy?.();
        lucidPreferenceControl = LucidGamePreference?.mount?.(
            document.getElementById('lucid-game-preference-slot'),
            status,
        );
    }

    function mountMangaPreference(status) {
        attachBattlePreferences();
        mangaPreferenceControl?.destroy?.();
        mangaPreferenceControl = MangaGamePreference?.mount?.(
            document.getElementById('manga-game-preference-slot'),
            status,
        ) || null;
    }

    /* The lobby hands over a status it is still reading as the read itself,
       so the dialog can open at once; each switch is mounted when its own
       status answers, inside the stage's hold. A failed read is an unknown
       status, exactly as when the lobby used to await it. */
    function settleBattleStatuses(generation, options) {
        const whenAnswered = (status, mount) => {
            if (!isPendingStatus(status)) return Promise.resolve();
            return Promise.resolve(status).catch(() => undefined).then((answer) => {
                if (isCurrentSession(generation)) mount(answer);
            });
        };
        return Promise.all([
            whenAnswered(options.lucidStatus, mountLucidPreference),
            whenAnswered(options.mangaStatus, mountMangaPreference),
        ]);
    }

    /**
     * Open and load the selector without creating a selection promise.
     * Product flows should use choose(); open() remains useful for surfaces
     * that own their lifecycle and for focused rendering tests.
     */
    async function open(options = {}) {
        const generation = beginSession(options);
        if (generation === null) return false;
        return loadSession(generation, options);
    }

    /**
     * Open the selector and resolve exactly once with a typed outcome.
     * Repeated calls while the same picker is visible share one choice.
     */
    function choose(options = {}) {
        if (isOpen && activeChoice) return activeChoice.promise;

        const generation = beginSession(options);
        if (generation === null) return Promise.resolve({ kind: 'blocked' });
        let resolveChoice;
        const promise = new Promise((resolve) => {
            resolveChoice = resolve;
        });
        activeChoice = { generation, promise, resolve: resolveChoice };
        loadSession(generation, options).catch((error) => {
            if (isCurrentSession(generation)) {
                console.error('[DeckSelector] Failed to prepare selector:', error);
            }
        });
        return promise;
    }

    function settleChoice(outcome, generation) {
        if (!activeChoice || activeChoice.generation !== generation) return;
        const { resolve } = activeChoice;
        activeChoice = null;
        resolve(outcome);
    }

    /**
     * Close the deck selector
     */
    function close({ outcome = { kind: 'cancelled' }, restoreFocus = outcome.kind === 'cancelled' } = {}) {
        if (!isOpen) return false;
        const closingGeneration = sessionGeneration;
        const focusTarget = returnFocusTo;
        isOpen = false;
        releaseLobbySurface();
        sessionGeneration += 1;
        sessionAbortController?.abort();
        sessionAbortController = null;
        const modal = document.getElementById('deck-selector-modal');
        if (modal) {
            modal.style.display = 'none';
        }
        const content = document.getElementById('ds-content-area');
        if (content) {
            content.replaceChildren();
            delete content.dataset.reveal;
            content.removeAttribute('aria-busy');
        }
        settleChoice(outcome, closingGeneration);
        excludedCharacter = null;
        returnFocusTo = null;
        resetSessionData();
        if (restoreFocus && focusTarget?.isConnected && typeof focusTarget.focus === 'function') {
            focusTarget.focus();
        }
        return true;
    }

    /**
     * Create the modal DOM elements if they don't exist
     */
    /* THE PREFERENCES ROW SURVIVES EVERY STAGE RENDER.

       A stage replaces its body (replaceStageBody keeps the row in place), and
       an error or a new session empties `#ds-content-area` outright, which
       detaches the row and the two mounted controls with it. Holding the
       ELEMENT in a variable keeps it (and its listeners) alive while detached,
       so the same node is re-appended afterwards; `order` in
       `deck-selector.css` then puts it between the header and the grid. The
       row cannot simply be authored into the stage markup — that HTML is
       rebuilt from a template string on every render.

       A MutationObserver does the re-attaching rather than a call after each
       render. Six scattered calls were tried first and two of them landed
       INSIDE a `.map()` callback: the stage templates contain nested template
       literals, so scanning for the closing backtick found a nested opening
       one instead. The switches vanished from the picker entirely. The
       observer cannot be misplaced, and it covers render paths added later. */
    let battlePreferencesEl = null;
    let contentAreaObserver = null;

    function attachBattlePreferences() {
        const content = document.getElementById('ds-content-area');
        if (!battlePreferencesEl) {
            battlePreferencesEl = document.querySelector('.battle-preferences');
        }
        if (content && battlePreferencesEl && battlePreferencesEl.parentElement !== content) {
            content.appendChild(battlePreferencesEl);
        }
    }

    function watchContentArea() {
        const content = document.getElementById('ds-content-area');
        if (!content || contentAreaObserver) return;
        /* The re-append is itself a childList mutation, but the parent check
           above makes the second pass a no-op, so this cannot loop. */
        contentAreaObserver = new MutationObserver(attachBattlePreferences);
        contentAreaObserver.observe(content, { childList: true });
        attachBattlePreferences();
    }

    function createModalIfNotExists() {
        if (document.getElementById('deck-selector-modal')) return;

        const modal = document.createElement('div');
        modal.id = 'deck-selector-modal';
        modal.className = 'deck-selector-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-label', uiText('deckSelector.chooseChampion', 'Choose Your Champion'));
        modal.innerHTML = `
            <div class="deck-selector-container">
                <button class="deck-selector-close" id="deck-selector-close" type="button" aria-label="${htmlText('manga.nav.close', 'Close')}">&times;</button>
                <div id="ds-content-area" class="ds-content-area">
                    <!-- Dynamic Content: Character Grid or Deck List -->
                </div>
                <div class="battle-preferences">
                    <div id="lucid-game-preference-slot" class="lucid-game-preference-slot"></div>
                    <div id="manga-game-preference-slot" class="manga-game-preference-slot"></div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        watchContentArea();
        document.getElementById('deck-selector-close').addEventListener('click', close);
        window.addEventListener('moonfall:viewport-change', handleViewportChange);
        window[viewportHandlerKey] = handleViewportChange;
        modal.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                close();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = getModalFocusableElements(modal);
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                focusWithoutScroll(last);
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                focusWithoutScroll(first);
            }
        });
    }

    /**
     * Load decks from Convex
     */
    function resetDeckPaginationState() {
        deckPage = 1;
        deckPageIsDone = true;
        deckPageCursors = new Map([[1, null]]);
        cachedDecks = [];
        deckLoadFailed = false;
        starterCatalogState = 'ok';
        deckLocalSlice = 0;
        deckLogicalPage = 1;
    }

    /* Set when a deck page could not be fetched, so the empty list can say
       "we could not load these" instead of "you have none". */
    let deckLoadFailed = false;
    /* "ok", or the server's reason the official starter decks are missing. */
    let starterCatalogState = 'ok';

    function usesPhoneDeckPages() {
        return Platform?.isMobileViewport?.() === true;
    }

    function characterKeys(character) {
        return [...new Set([
            character?.id,
            character?.characterId,
        ].filter(Boolean))];
    }

    function selectedCharacterKeys() {
        return characterKeys(selectedCharacter);
    }

    function requestDecksPage(keys, cursor) {
        if (!MoonfallCloud || !MoonfallCloud.decks) {
            throw new Error('Cloud services not initialized');
        }
        if (typeof MoonfallCloud.decks.listPlayable !== 'function') {
            throw new Error('Playable deck pagination is unavailable');
        }
        return MoonfallCloud.decks.listPlayable(keys, cursor || null, DECK_PAGE_SIZE);
    }

    function applyDecksPage(normalizedPage, result) {
        cachedDecks = Array.isArray(result?.page) ? result.page : [];
        /* A DE-LISTED STARTER CATALOGUE IS A FAULT, NOT AN EMPTY ACCOUNT.

           `decks:listPlayableDecks` reports `starterCatalog` now. Anything
           but "ok" means the server could not serve the 21 official starter
           decks — a release-state/pack-hash mismatch after a deploy, or a
           summoner whose key does not resolve — and every one of the seven
           vanilla summoners loses its three starters at once. That used to
           arrive as an empty array and render as "No saved decks. Create
           one!", which is how it reached the owner as "all 7 or so vanilla
           summoners had 3 predefined official decks each. those werent
           showing up either." */
        starterCatalogState = typeof result?.starterCatalog === 'string'
            ? result.starterCatalog
            : 'ok';
        if (starterCatalogState !== 'ok') {
            console.error('[DeckSelector] official starter catalogue unavailable:', starterCatalogState);
        }
        deckPage = normalizedPage;
        deckPageIsDone = Boolean(result?.isDone);
        const nextCursor = typeof result?.continueCursor === 'string'
            ? result.continueCursor
            : null;
        if (nextCursor) {
            deckPageCursors.set(normalizedPage + 1, nextCursor);
        } else {
            deckPageCursors.delete(normalizedPage + 1);
        }
    }

    /* A FAILED LOAD IS NOT AN EMPTY COLLECTION.

       This set `cachedDecks = []` and returned, so every failure —
       a dropped Convex query, an auth blip, the server rejecting the
       character keys — rendered as "No saved decks. Create one!" with
       nothing but Random Deck beside it. The player is then told,
       confidently and wrongly, that the decks they built do not exist,
       and the only trace is a console line they will never see.

       The flag lets the renderer say what actually happened and offer a
       retry. AGENTS.md: prefer explicit failure or a visible degraded
       state over a fallback that pretends the system is healthy. */
    function applyDecksFailure() {
        cachedDecks = [];
        deckLoadFailed = true;
        deckPageIsDone = true;
    }

    async function loadDecksPage(targetPage = 1, { force = false, generation = sessionGeneration } = {}) {
        if (!isCurrentSession(generation) || !selectedCharacter) return false;
        if (force) resetDeckPaginationState();
        const normalizedPage = Math.max(1, Number(targetPage) || 1);
        const cursor = deckPageCursors.has(normalizedPage)
            ? deckPageCursors.get(normalizedPage)
            : undefined;
        if (normalizedPage > 1 && cursor === undefined) return false;
        const requestedCharacterId = selectedCharacter.id;
        deckLoadFailed = false;
        starterCatalogState = 'ok';
        try {
            const result = await requestDecksPage(selectedCharacterKeys(), cursor);
            if (!isCurrentSession(generation) || selectedCharacter?.id !== requestedCharacterId) return false;
            applyDecksPage(normalizedPage, result);
            return true;
        } catch (error) {
            if (isCurrentSession(generation)) {
                console.error('[DeckSelector] Failed to load decks:', error);
            }
            applyDecksFailure();
            return false;
        }
    }

    /* The deck lists are the ACCOUNT's, kept across opens: keyed by the
       signed-in player, never presented to another. */
    function deckReadKey(character) {
        const uid = Firebase?.getUser?.()?.uid || '';
        return `${uid}|${character?.id || ''}`;
    }

    function readDecksCapped(character) {
        const reading = { settled: false, ok: false, result: null, error: null, promise: null, at: 0 };
        reading.promise = new Promise((resolve) => {
            const settle = (ok, value) => {
                if (reading.settled) return;
                window.clearTimeout(timer);
                reading.settled = true;
                reading.ok = ok;
                reading.at = Date.now();
                if (ok) reading.result = value;
                else reading.error = value;
                resolve();
            };
            const timer = window.setTimeout(
                () => settle(false, namedError('TimeoutError', 'Deck read timed out')),
                DECK_READ_TIMEOUT_MS,
            );
            let request;
            try {
                request = Promise.resolve(requestDecksPage(characterKeys(character), null));
            } catch (error) {
                request = Promise.reject(error);
            }
            request.then((result) => settle(true, result), (error) => settle(false, error));
        });
        return reading;
    }

    /**
     * The first deck page of one summoner: read by the lobby for every
     * summoner of the roster (warmRosterAhead), while the grid is open (at
     * idle, and at once on hover/focus/touch of its card), so that choosing
     * it opens the deck stage in the same frame. Kept for the account across
     * opens; a failed read is simply read again next time, and a deck made,
     * changed or deleted drops every list (`moonfall:decks-changed`). Capped:
     * a read that never answers settles as a failure, which the deck stage
     * shows with its retry, rather than leaving the card busy forever.
     */
    function warmCharacterDecks(character) {
        const key = deckReadKey(character);
        const existing = deckReads.get(key);
        if (existing && !(existing.settled && !existing.ok)) return existing;
        const reading = readDecksCapped(character);
        deckReads.set(key, reading);
        return reading;
    }

    /* What the player sees of a deck list: a re-read that changes none of it changed nothing. */
    function deckListSignature(result) {
        return JSON.stringify([
            (Array.isArray(result?.page) ? result.page : []).map((deck) => [
                deck?.id ?? null,
                deck?.name ?? null,
                deck?.character ?? null,
                Number.isFinite(deck?.cardCount) ? deck.cardCount : (Array.isArray(deck?.cardIds) ? deck.cardIds.length : null),
                Boolean(deck?.isOfficialStarter),
                deck?.templateId ?? null,
            ]),
            result?.continueCursor ?? null,
            Boolean(result?.isDone),
        ]);
    }

    /**
     * A kept deck list older than DECK_FRESH_MS, read again behind the view:
     * the kept one answers a press meanwhile, and an answer that differs
     * replaces it — and the deck stage showing it, in place, on its first
     * page (a page the player turned to keeps its own read).
     */
    function revalidateCharacterDecks(character, generation) {
        const key = deckReadKey(character);
        const kept = deckReads.get(key);
        if (!kept?.settled || !kept.ok || kept.revalidating) return;
        if (Date.now() - kept.at < DECK_FRESH_MS) return;
        kept.revalidating = true;
        const fresh = readDecksCapped(character);
        void fresh.promise.then(() => {
            kept.revalidating = false;
            // Dropped or replaced meanwhile: this answer is nobody's.
            if (deckReads.get(key) !== kept || !fresh.ok) return;
            if (deckListSignature(fresh.result) === deckListSignature(kept.result)) {
                kept.at = fresh.at;
                return;
            }
            deckReads.set(key, fresh);
            if (
                isCurrentSession(generation)
                && selectedCharacter?.id === character.id
                && document.getElementById('ds-content-area')?.dataset.stage === 'decks'
                && deckPage === 1
                && deckLocalSlice === 0
                && !deckLoadFailed
            ) {
                const focusedId = document.activeElement?.id || 'ds-back-btn';
                applyDecksPage(1, fresh.result);
                renderSelectedCharacterDecks(generation, focusedId);
            }
        });
    }

    /* What the player can do next from a grid: choose one of its summoners
       (their decks) or turn the page (the next page, pictures included). */
    function scheduleGridWarms(generation, renderGeneration) {
        const stillShowing = () => (
            isCurrentSession(generation) && renderGeneration === portraitRenderGeneration
        );
        whenIdle(() => {
            if (!stillShowing()) return;
            if (!characterPageIsDone) prepareCharacterPage(characterPage + 1);
            const queue = charactersData.filter((char) => char.id !== excludedCharacter);
            let next = 0;
            const worker = async () => {
                while (next < queue.length && stillShowing()) {
                    const character = queue[next];
                    next += 1;
                    const reading = warmCharacterDecks(character);
                    revalidateCharacterDecks(character, generation);
                    await reading.promise;
                }
            };
            for (let index = 0; index < Math.min(DECK_WARM_CONCURRENCY, queue.length); index += 1) {
                void worker();
            }
        });
    }

    function whenIdle(task) {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(task, { timeout: 1000 });
        } else {
            window.setTimeout(task, 200);
        }
    }

    async function handleViewportChange() {
        /* The deck stage only: a summoner still opening from the grid owns its
           own read, and a resize must not jump it to the decks early. */
        if (!isOpen || !selectedCharacter) return;
        if (document.getElementById('ds-content-area')?.dataset.stage !== 'decks') return;
        const generation = sessionGeneration;
        const characterId = selectedCharacter.id;
        const loaded = await loadDecksPage(1, { force: true, generation });
        if (!loaded || !isCurrentSession(generation) || selectedCharacter?.id !== characterId) return;
        renderSelectedCharacterDecks(generation);
    }

    /**
     * Load characters from API
     */
    function resetCharacterPaginationState() {
        characterPage = 1;
        characterPageIsDone = true;
        characterPageCursors = new Map([[1, null]]);
        characterPortraitByKey = new Map();
        charactersData = [];
        preparedPages = new Map();
        pageTurn = null;
    }

    function normalizeCharacters(list) {
        return list.map(char => ({
            id: char.deckKey || (char.name ? char.name.split(' ')[0] : char.characterId),
            characterId: char.characterId || null,
            name: char.name,
            description: char.title || char.bio,
            picture: char.picture,
            imageUrl: sanitizeImageUrlForDirectClient(char.imageUrl),
            theme: char.name ? char.name.split(' ')[0].toLowerCase() : 'moon'
        }));
    }

    /** A page of the grid from route rows, in the route's order. */
    function pageOfRows(list, { isDone, nextCursor }) {
        const characters = normalizeCharacters(list);
        const portraitByKey = new Map();
        characters.forEach((char, index) => {
            const source = list[index] || {};
            const characterId = source.characterId || null;
            if (char.id) {
                portraitByKey.set(char.id, char.imageUrl || null);
            }
            if (characterId) {
                portraitByKey.set(characterId, char.imageUrl || null);
            }
        });
        return { characters, portraitByKey, isDone, nextCursor };
    }

    /* A page past the roster, from the route's own cursor. */
    async function fetchCharacterPage(cursor, signal = null) {
        const answer = await SummonerRoster.fetchRows(cursor, { limit: CHARACTER_PAGE_SIZE, signal });
        return pageOfRows(answer.rows, { isDone: answer.isDone, nextCursor: answer.nextCursor });
    }

    /**
     * Page `pageNumber` cut from the roster, or null when the roster ends
     * before it. The last page the roster covers leads on through the route's
     * own cursor when the roster is not the whole list (an account past the
     * route's cap for one read); every other page leads on to the roster's
     * next page (ROSTER_PAGE).
     */
    function rosterPage(roster, pageNumber) {
        const rows = Array.isArray(roster?.rows) ? roster.rows : [];
        const start = (pageNumber - 1) * CHARACTER_PAGE_SIZE;
        if (pageNumber > 1 && start >= rows.length) return null;
        const end = Math.min(rows.length, start + CHARACTER_PAGE_SIZE);
        const last = end >= rows.length;
        const done = last && (roster.isDone || !roster.nextCursor);
        return pageOfRows(rows.slice(start, end), {
            isDone: done,
            nextCursor: done ? null : (last ? roster.nextCursor : ROSTER_PAGE),
        });
    }

    /* A roster page, reading the roster first when none is kept (a read a
       session starts is that session's: summoner-roster.js drops it if the
       session closes first). A page the roster ends before is an empty page:
       the end of the list. */
    function readRosterPage(pageNumber, signal = null) {
        return SummonerRoster.read({ signal }).promise.then((roster) => (
            rosterPage(roster, pageNumber) || pageOfRows([], { isDone: true, nextCursor: null })
        ));
    }

    /* Whether page `pageNumber` of the pagination being walked is the
       roster's to answer (page one always is). */
    function isRosterPage(pageNumber, cursor) {
        return pageNumber === 1 || cursor === ROSTER_PAGE;
    }

    /* AN EMPTY PAGE AFTER PAGE ONE IS THE END OF THE LIST, not a failure.
       Cursor paging only learns it is done by looking past the last row, so a
       page that ends exactly on the player's last own summoner still reports
       more, and the next request comes back empty. Seen in a player's
       recording (2026-09-22): the dialog was replaced by "Failed to load
       characters". The page on screen was the last one: keep it and retire
       Next. */
    function isEmptyLaterPage(pageNumber, page) {
        return pageNumber > 1 && !(page?.characters?.length);
    }

    function retireEmptyPage(pageNumber) {
        preparedPages.delete(pageNumber);
        characterPageCursors.delete(pageNumber);
        if (characterPage === pageNumber - 1) {
            characterPageIsDone = true;
            updateCharacterPaginationControls();
        }
    }

    function applyCharacterPage(pageNumber, page) {
        preparedPages.set(pageNumber, { page, promise: Promise.resolve(page) });
        charactersData = page.characters.slice();
        characterPortraitByKey = new Map(page.portraitByKey);
        characterPage = pageNumber;
        characterPageIsDone = page.isDone;
        if (page.nextCursor) {
            characterPageCursors.set(pageNumber + 1, page.nextCursor);
        } else {
            characterPageCursors.delete(pageNumber + 1);
        }
    }

    /* ---- page turns -------------------------------------------------------

       The pager used to wait for the next page's data, draw four empty wells
       and let the portraits land one at a time. The next page is now read
       while the current one is on screen (law 3: "subsequent pages prefetch at
       idle priority while the current page is viewed"), its portraits fetched
       and decoded, and a page already seen is kept, so Next and Prev swap a
       finished page in one step. A turn that beats its read keeps the current
       page on screen, answers on the pressed button, and swaps whole once the
       page's pictures are in, capped like the first page. */
    function prepareCharacterPage(pageNumber) {
        const existing = preparedPages.get(pageNumber);
        if (existing) return existing;
        const cursor = characterPageCursors.get(pageNumber);
        if (pageNumber > 1 && !cursor) return null;
        const signal = sessionAbortController?.signal || null;
        /* `passedThrough`: a fast turn is only walking through this page to
           reach its cursor; its pictures are not about to be seen, so they
           are not fetched (a later turn that lands here fetches them). */
        const entry = { page: null, promise: null, passedThrough: false };
        const cursors = characterPageCursors;
        /* A page the kept roster holds is cut from it in this task, so a
           turn to it whose portraits are decoded lands in the press's own
           task (turnCharacterPage). */
        const kept = isRosterPage(pageNumber, cursor) ? SummonerRoster.peek() : null;
        const cut = kept ? rosterPage(kept, pageNumber) : null;
        if (cut) entry.page = cut;
        const read = cut
            ? Promise.resolve(cut)
            : (isRosterPage(pageNumber, cursor)
                ? readRosterPage(pageNumber, signal)
                : fetchCharacterPage(cursor, signal));
        entry.promise = read.then((page) => {
            entry.page = page;
            // The page's own cursor leads to the one after it, whether or not
            // it is ever shown (a fast turn walks through it). Only into the
            // pagination it was read for: a reset since then started another.
            if (page?.nextCursor && cursors === characterPageCursors && !cursors.has(pageNumber + 1)) {
                cursors.set(pageNumber + 1, page.nextCursor);
            }
            if (!entry.passedThrough) void warmPortraits(portraitSourcesOf(page));
            return page;
        }, (error) => {
            if (preparedPages.get(pageNumber) === entry) preparedPages.delete(pageNumber);
            throw error;
        });
        entry.promise.catch(() => { /* the turn that needs it reports it */ });
        preparedPages.set(pageNumber, entry);
        return entry;
    }

    function markPageTurning(button, busy) {
        if (!button?.isConnected) return;
        button.classList.toggle('btn-pending', busy);
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
    }

    function settleWithinCap(task, capMs) {
        return new Promise((resolve) => {
            const timer = window.setTimeout(resolve, capMs);
            const done = () => {
                window.clearTimeout(timer);
                resolve();
            };
            Promise.resolve(task).then(done, done);
        });
    }

    /* FAST HANDS. A press while a turn is still reading is received, not
       dropped: the turning button keeps taking presses (deck-selector.css),
       and each Next or Prev moves the turn on from the page it is heading to.
       The turn then walks there: a page it only passes through is read for
       its cursor and never painted, nor are its pictures fetched. Round 15
       (28 September 2026): the second of two quick Nexts was swallowed —
       never acknowledged on Chrome desktop, answered only 0.7-1.5 s later on
       the phones — and the player was left a page short. */
    function turnTarget() {
        return pageTurn ? pageTurn.target : characterPage;
    }

    /* Whether the page a turn is heading to (or the one on screen) is known
       to be the last. Unknown is not last: a page still being read may lead
       further, and the turn stops at the real end if it does not. */
    function targetIsLastPage() {
        if (!pageTurn) return characterPageIsDone;
        return preparedPages.get(pageTurn.target)?.page?.isDone === true;
    }

    function retargetPageTurn(turn, targetPage, button) {
        /* Moving on past the page it was heading to: that page is only walked
           through now, so its pictures are not fetched. */
        const passing = preparedPages.get(turn.target);
        if (targetPage > turn.target && passing && !passing.page) passing.passedThrough = true;
        turn.target = targetPage;
        if (turn.button !== button) {
            markPageTurning(turn.button, false);
            turn.button = button;
            markPageTurning(button, true);
        }
        // A walk waiting on the pictures of the page it was heading to moves on now.
        turn.moved?.();
    }

    /* The page the turn must read next on its way to `targetPage`: that page
       itself once its cursor is known, otherwise the furthest page before it
       that can be read (its cursor leads on). Its entry is null when even
       that cannot be read. */
    function nextPageOnTheWay(targetPage) {
        let number = targetPage;
        while (number > 1 && !characterPageCursors.has(number) && !preparedPages.has(number)) {
            number -= 1;
        }
        const entry = prepareCharacterPage(number);
        if (entry && number !== targetPage && !entry.page) entry.passedThrough = true;
        return { number, entry };
    }

    /* Resolves with the page the turn lands on, or null when it was taken
       over (a new grid, a closed dialog) or its way is unknown. */
    async function walkPageTurn(turn) {
        for (;;) {
            if (pageTurn !== turn) return null;
            const wanted = turn.target;
            const { number, entry } = nextPageOnTheWay(wanted);
            if (!entry) return null;
            const page = await entry.promise;
            if (pageTurn !== turn) return null;
            if (isEmptyLaterPage(number, page)) {
                // The list ended on the page before this one: stop there.
                retireEmptyPage(number);
                turn.target = Math.min(turn.target, number - 1);
                continue;
            }
            if (turn.target !== wanted) continue;
            if (number !== wanted) {
                // Walked through: the list may end here, before the target.
                if (page.isDone || !characterPageCursors.has(number + 1)) turn.target = number;
                continue;
            }
            /* THE WAIT FOR ITS PICTURES ENDS WITH A PRESS THAT MOVES THE TURN
               ON (review of wave D, 28 September 2026): a Next pressed while
               this page's pictures were still arriving used to wait them out
               — up to the stage cap on a slow link — before the page the
               player now wanted was even asked for. */
            const moved = new Promise((resolve) => { turn.moved = resolve; });
            await Promise.race([
                settleWithinCap(warmPortraits(portraitSourcesOf(page)), STAGE_REVEAL_CAP_MS),
                moved,
            ]);
            turn.moved = null;
            if (pageTurn !== turn) return null;
            if (turn.target !== wanted) continue;
            return { number, page };
        }
    }

    function endPageTurn(turn) {
        if (pageTurn !== turn) return;
        pageTurn = null;
        turn.moved?.();
        markPageTurning(turn.button, false);
    }

    async function turnCharacterPage(targetPage, button, generation) {
        if (!isCurrentSession(generation)) return;
        if (pageTurn) {
            // Taken back to the page on screen: the turn simply ends.
            if (targetPage === characterPage) endPageTurn(pageTurn);
            else retargetPageTurn(pageTurn, targetPage, button);
            return;
        }
        const fromPage = characterPage;
        if (targetPage === fromPage) return;
        const prepared = prepareCharacterPage(targetPage);
        if (!prepared) return;
        let landed = prepared.page && pagePortraitsReady(prepared.page)
            ? { number: targetPage, page: prepared.page }
            : null;
        if (!landed) {
            /** @type {{ target: number, button: HTMLElement, moved: (() => void) | null }} */
            const turn = { target: targetPage, button, moved: null };
            pageTurn = turn;
            markPageTurning(button, true);
            try {
                landed = await walkPageTurn(turn);
            } catch (error) {
                landed = null;
                if (error?.name !== 'AbortError') {
                    console.error('[DeckSelector] Failed to load characters:', error);
                }
            }
            if (pageTurn !== turn) return;
            endPageTurn(turn);
            if (!landed || !isCurrentSession(generation) || characterPage !== fromPage) return;
            // The list ended on the page on screen: nothing to swap.
            if (landed.number === fromPage) return;
        }
        if (isEmptyLaterPage(landed.number, landed.page)) {
            retireEmptyPage(landed.number);
            return;
        }
        applyCharacterPage(landed.number, landed.page);
        renderCharacterSelection(generation);
    }

    async function loadCharactersPage(targetPage = 1, { force = false, generation = sessionGeneration } = {}) {
        if (!isCurrentSession(generation)) return false;
        if (force) {
            resetCharacterPaginationState();
        }
        const normalizedPage = Math.max(1, Number(targetPage) || 1);
        const cursor = characterPageCursors.has(normalizedPage)
            ? characterPageCursors.get(normalizedPage)
            : undefined;
        if (normalizedPage > 1 && cursor === undefined) {
            return false;
        }
        try {
            const signal = sessionAbortController?.signal || null;
            const page = isRosterPage(normalizedPage, cursor)
                ? await readRosterPage(normalizedPage, signal)
                : await fetchCharacterPage(cursor, signal);
            if (!isCurrentSession(generation)) return false;
            if (isEmptyLaterPage(normalizedPage, page)) {
                retireEmptyPage(normalizedPage);
                return false;
            }
            applyCharacterPage(normalizedPage, page);
            return true;
        } catch (error) {
            if (!isCurrentSession(generation)) return false;
            if (error?.name !== 'AbortError') {
                console.error('[DeckSelector] Failed to load characters:', error);
            }
            charactersData = [];
            characterPageIsDone = true;
            return false;
        }
    }

    function sanitizeImageUrlForDirectClient(raw) {
        if (!raw || typeof raw !== 'string') return null;
        const value = raw.trim();
        if (!value) return null;
        try {
            let pathname = new URL(
                value,
                window.location?.href || 'http://localhost/',
            ).pathname;
            for (let count = 0; count < 4; count += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            if (pathname === '/assets/art_placeholder.webp') return null;
        } catch (_) {
            return null;
        }
        if (value.startsWith('/api/card-image/')) return null;
        if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) {
            return value;
        }
        if (value.startsWith('/assets/') || value.startsWith('assets/')) {
            return value;
        }
        return null;
    }

    /**
     * A pagination band earns its space only when it can actually go somewhere.
     * Two dead buttons and an unchangeable "Page 1" cost 52px — on a 288pt phone
     * page box that is the difference between a list that fits and one the
     * player has to scroll while ALSO looking at a pager.
     *
     * `items` is optional: the character grid knows how many it drew, the deck
     * list is judged on its cursor alone.
     */
    function pagerIsUseful({ page, isDone, items } = {}) {
        if (items !== undefined && !(Number(items) > 0)) return false;
        const currentPage = Number(page);
        return (Number.isFinite(currentPage) && currentPage > 1) || isDone === false;
    }

    function updateCharacterPaginationControls() {
        const prevBtn = document.getElementById('ds-character-prev');
        const nextBtn = document.getElementById('ds-character-next');
        const pageInfo = document.getElementById('ds-character-page-info');
        const controls = document.getElementById('ds-character-pagination');
        const totalItems = Array.isArray(charactersData) ? charactersData.length : 0;
        if (controls) {
            controls.style.display = pagerIsUseful({
                page: characterPage,
                isDone: characterPageIsDone,
                items: totalItems,
            }) ? 'flex' : 'none';
        }
        if (prevBtn) prevBtn.disabled = characterPage <= 1;
        if (nextBtn) nextBtn.disabled = characterPageIsDone;
        if (pageInfo) {
            pageInfo.textContent = uiText('common.page', `Page ${characterPage}`, { page: characterPage });
        }
    }

    function getCharacterPortraitUrl(char) {
        const directUrl = sanitizeImageUrlForDirectClient(char?.imageUrl);
        if (directUrl) return directUrl;
        const byId = sanitizeImageUrlForDirectClient(characterPortraitByKey.get(char?.id));
        if (byId) return byId;
        return '';
    }

    function waitForImageLoad(image, timeoutMs, renderSignal = null) {
        if (renderSignal?.aborted) {
            return Promise.reject(namedError('AbortError', 'Portrait render was cancelled'));
        }
        if (image.complete) {
            return image.naturalWidth > 0 && image.naturalHeight > 0
                ? Promise.resolve()
                : Promise.reject(new Error('Image failed to load'));
        }
        return new Promise((resolve, reject) => {
            let settled = false;
            const finish = (error = null) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeoutId);
                image.removeEventListener('load', handleLoad);
                image.removeEventListener('error', handleError);
                renderSignal?.removeEventListener('abort', handleAbort);
                if (error) reject(error);
                else resolve();
            };
            const handleLoad = () => finish();
            const handleError = () => finish(new Error('Image failed to load'));
            const handleAbort = () => finish(namedError('AbortError', 'Portrait render was cancelled'));
            const timeoutId = window.setTimeout(() => {
                const error = new Error('Image load timed out');
                error.name = 'TimeoutError';
                finish(error);
            }, timeoutMs);
            image.addEventListener('load', handleLoad, { once: true });
            image.addEventListener('error', handleError, { once: true });
            renderSignal?.addEventListener('abort', handleAbort, { once: true });
        });
    }

    function withTimeout(task, timeoutMs) {
        return new Promise((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                const error = new Error('Image decode timed out');
                error.name = 'TimeoutError';
                reject(error);
            }, timeoutMs);
            Promise.resolve(task).then(
                (value) => {
                    window.clearTimeout(timeoutId);
                    resolve(value);
                },
                (error) => {
                    window.clearTimeout(timeoutId);
                    reject(error);
                },
            );
        });
    }

    function namedError(name, message) {
        const error = new Error(message);
        error.name = name;
        return error;
    }

    function isRemotePortraitUrl(url) {
        return /^https?:\/\//i.test(String(url || ''));
    }

    function readCachedPortraitBlob(url) {
        const cached = portraitBlobCache.get(url);
        if (!cached) return null;
        portraitBlobCache.delete(url);
        portraitBlobCache.set(url, cached);
        return cached.blob;
    }

    function cachePortraitBlob(url, blob) {
        const size = Number(blob?.size) || 0;
        if (!url || !blob || size <= 0 || size > CHARACTER_PORTRAIT_BLOB_CACHE_MAX_BYTES) return;
        const existing = portraitBlobCache.get(url);
        if (existing) {
            portraitBlobCacheBytes -= existing.size;
            portraitBlobCache.delete(url);
        }
        portraitBlobCache.set(url, { blob, size });
        portraitBlobCacheBytes += size;
        while (
            portraitBlobCache.size > CHARACTER_PORTRAIT_BLOB_CACHE_MAX_ENTRIES
            || portraitBlobCacheBytes > CHARACTER_PORTRAIT_BLOB_CACHE_MAX_BYTES
        ) {
            const oldestUrl = portraitBlobCache.keys().next().value;
            const oldest = portraitBlobCache.get(oldestUrl);
            portraitBlobCache.delete(oldestUrl);
            portraitBlobCacheBytes -= oldest?.size || 0;
        }
    }

    function deleteCachedPortraitBlob(url) {
        const cached = portraitBlobCache.get(url);
        if (!cached) return;
        portraitBlobCache.delete(url);
        portraitBlobCacheBytes = Math.max(0, portraitBlobCacheBytes - cached.size);
    }

    /* ---- a warm yields to the screen the player opened ---------------------

       The warm is for the lobby's next press. Another lobby surface open over
       the lobby (LobbyCover: the Collection, the Foundry, the Store, Feedback,
       Referrals) is what the player is looking at. So a warm starts no
       portrait while one is open (warmPortraitBytes), and one on the wire when
       it opens is stopped unless three quarters of it is in: the load
       scheduler's own rule (load-scheduler.ts PREEMPT_BELOW), since the last
       quarter costs less to finish than the whole costs to fetch again. On
       1 Mbps an 835 KB portrait stopped half in hands the page on screen
       418 KB, 3.3 s of the whole link, and the warm keeps two on the wire. It
       is asked for again once the surface closes, from the cache: the art host
       sends a strong validator and byte ranges, so Chrome keeps the part that
       arrived and asks for the rest. The picker's own dialog is not another
       surface; it waits for this warm (fetchPortraitBlobWithRetry). */
    const PORTRAIT_YIELD_BELOW = 0.75;

    function coveredByAnotherSurface() {
        return LobbyCover.isCovered({ except: SURFACE_OWNER });
    }

    /* Nothing has answered, nothing has arrived, or less than three quarters
       of a known length. An answer of unknown length finishes: its progress
       cannot be judged (the scheduler's rule for such a download). */
    function cheaperToStop(progress) {
        if (!progress.answered || progress.received === 0) return true;
        return progress.total > 0 && progress.received / progress.total < PORTRAIT_YIELD_BELOW;
    }

    /* The body as it arrives, so a yield can tell how much of it is in. */
    async function readPortraitBody(response, progress) {
        const reader = response.body?.getReader?.();
        if (!reader) return response.blob();
        const length = Number(response.headers?.get?.('content-length'));
        if (Number.isFinite(length) && length > 0) progress.total = length;
        const chunks = [];
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            progress.received += value?.byteLength || 0;
        }
        return new Blob(chunks, { type: response.headers?.get?.('content-type') || '' });
    }

    /**
     * One attempt at a portrait's bytes, cut off at `timeoutMs` (the image
     * attempt ladder). With `yieldToCover`, another surface opening over the
     * lobby stops it while that is cheaper than finishing it: a YieldError,
     * which is not a failed attempt.
     */
    async function fetchPortraitBlob(url, timeoutMs, renderSignal, cacheMode, { yieldToCover = false } = {}) {
        if (renderSignal?.aborted) {
            throw namedError('AbortError', 'Portrait render was cancelled');
        }

        const controller = new AbortController();
        let timedOut = false;
        let yielded = false;
        const progress = { answered: false, received: 0, total: 0 };
        const cancelForRender = () => controller.abort();
        renderSignal?.addEventListener('abort', cancelForRender, { once: true });
        const timeoutId = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, timeoutMs);
        const yieldToSurface = () => {
            if (yielded || !coveredByAnotherSurface() || !cheaperToStop(progress)) return;
            yielded = true;
            controller.abort();
        };
        const unwatchCover = yieldToCover ? LobbyCover.onChange(yieldToSurface) : null;

        try {
            const response = await fetch(url, {
                // Reuse a healthy browser response on the first attempt. A
                // retry explicitly reloads so an incomplete cached transfer
                // cannot trap the atomic page behind the same broken entry.
                cache: cacheMode,
                credentials: 'omit',
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error(`Portrait request failed (${response.status || 'unknown'})`);
            }
            progress.answered = true;
            const blob = yieldToCover ? await readPortraitBody(response, progress) : await response.blob();
            if (!blob || blob.size <= 0) {
                throw new Error('Portrait response was empty');
            }
            return blob;
        } catch (error) {
            if (renderSignal?.aborted) {
                throw namedError('AbortError', 'Portrait render was cancelled');
            }
            if (yielded) {
                throw namedError('YieldError', 'Portrait warm yielded to a lobby surface');
            }
            if (timedOut) {
                throw namedError('TimeoutError', 'Portrait request timed out');
            }
            throw error;
        } finally {
            window.clearTimeout(timeoutId);
            renderSignal?.removeEventListener('abort', cancelForRender);
            unwatchCover?.();
        }
    }

    async function decodeCharacterPortrait(image, loadTimeoutMs, renderSignal = null) {
        await waitForImageLoad(image, loadTimeoutMs, renderSignal);
        if (renderSignal?.aborted) {
            throw namedError('AbortError', 'Portrait render was cancelled');
        }
        if (typeof image.decode === 'function') {
            try {
                await withTimeout(image.decode(), CHARACTER_PORTRAIT_DECODE_TIMEOUT_MS);
            } catch (error) {
                // Once the load event has completed with intrinsic pixels, the
                // browser can paint the image even when decode() rejects or
                // never settles (observed in Firefox and Chromium). This is the
                // same drawable-authority contract as AssetPreloader.
                if (!image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
                    throw error;
                }
            }
        }
    }

    function releasePortraitObjectUrl(image) {
        const objectUrl = image?.dataset?.portraitObjectUrl;
        if (!objectUrl) return;
        /* Only a URL this session still owns. A decoded portrait handed to the
           store belongs to it now, and revoking its URL would blank it for
           good on its next adoption. */
        if (!activePortraitObjectUrls.has(objectUrl)) return;
        activePortraitObjectUrls.delete(objectUrl);
        try {
            URL.revokeObjectURL(objectUrl);
        } catch (_) {
            // The page-wide cleanup remains authoritative.
        }
        delete image.dataset.portraitObjectUrl;
    }

    function updateCharacterPortraitGridState(image) {
        const grid = image?.closest?.('.ds-character-grid');
        if (!grid?.isConnected || grid.getAttribute('aria-busy') === 'true') return;
        grid.dataset.portraitState = grid.querySelector('.ds-char-image-retrying')
            ? 'retrying'
            : 'ready';
    }

    function clearPortraitRetry(image, { disposeLoader = false } = {}) {
        const record = portraitRetryRecords.get(image);
        if (!record) return;
        if (record.timerId !== null) window.clearTimeout(record.timerId);
        record.signal?.removeEventListener('abort', record.abortHandler);
        portraitRetryRecords.delete(image);
        if (disposeLoader) Cards?.disposeCardArtImage?.(image);
    }

    function scheduleCharacterPortraitRetry(image, renderSignal) {
        const originalSrc = image?.dataset?.originalSrc || '';
        if (!originalSrc || renderSignal?.aborted || !image?.isConnected) return false;

        clearPortraitRetry(image, { disposeLoader: true });
        releasePortraitObjectUrl(image);
        image.removeAttribute('src');
        delete image.dataset.portraitSettled;
        delete image.dataset.fallbackReason;
        image.dataset.portraitSource = 'retrying-real';
        image.classList.remove('ds-char-image-unavailable', 'ds-char-image-fallback');
        image.classList.add('ds-char-image-retrying');

        const record = {
            signal: renderSignal,
            originalSrc,
            timerId: null,
            abortHandler: null,
        };
        const ownsRetry = () => (
            portraitRetryRecords.get(image) === record
            && !renderSignal?.aborted
            && image.isConnected
            && image.dataset.originalSrc === originalSrc
        );
        const beginSharedRetry = () => {
            record.timerId = null;
            if (!ownsRetry()) {
                if (portraitRetryRecords.get(image) === record) {
                    clearPortraitRetry(image, { disposeLoader: true });
                }
                return;
            }
            const loadRealImage = Cards?.loadCardArtImage;
            if (typeof loadRealImage !== 'function') {
                // cards.js precedes this module in production. Keep the well
                // retryable during isolated/hot module loading rather than
                // inventing a logo or declaring the real source unavailable.
                record.timerId = window.setTimeout(beginSharedRetry, 1000);
                return;
            }
            const readiness = loadRealImage(
                image,
                originalSrc,
                { fit: 'contain', positionX: 0.5, positionY: 0.5 },
                { directArtOnly: true },
            );
            void Promise.resolve(readiness).then((ready) => {
                if (!ownsRetry()) {
                    if (portraitRetryRecords.get(image) === record) {
                        clearPortraitRetry(image, { disposeLoader: true });
                    }
                    return;
                }
                if (ready !== true) {
                    record.timerId = window.setTimeout(beginSharedRetry, 1000);
                    return;
                }
                clearPortraitRetry(image);
                image.dataset.portraitSource = 'direct-retry';
                image.dataset.portraitSettled = 'true';
                image.classList.remove(
                    'ds-char-image-retrying',
                    'ds-char-image-unavailable',
                    'ds-char-image-fallback',
                );
                updateCharacterPortraitGridState(image);
            });
        };
        record.abortHandler = () => clearPortraitRetry(image, { disposeLoader: true });
        renderSignal?.addEventListener('abort', record.abortHandler, { once: true });
        portraitRetryRecords.set(image, record);
        beginSharedRetry();
        return true;
    }

    async function fetchPortraitBlobWithRetry(image, originalSrc, renderSignal) {
        const cached = readCachedPortraitBlob(originalSrc);
        if (cached) {
            image.dataset.portraitLoadAttempt = 'cache';
            return cached;
        }
        /* The warm is already downloading these bytes: join it rather than
           racing it for the same picture. */
        const warming = portraitBlobWarms.get(originalSrc);
        if (warming) {
            image.dataset.portraitLoadAttempt = 'warm';
            return untilAborted(warming, renderSignal);
        }
        image.dataset.portraitLoadAttempt = '1';
        try {
            const blob = await fetchPortraitBlob(
                originalSrc,
                imageAttemptDeadlineMs(0),
                renderSignal,
                'default',
            );
            cachePortraitBlob(originalSrc, blob);
            return blob;
        } catch (initialError) {
            if (renderSignal?.aborted || !image.isConnected) throw initialError;
            image.dataset.portraitLoadAttempt = '2';
            const blob = await fetchPortraitBlob(
                originalSrc,
                imageAttemptDeadlineMs(1),
                renderSignal,
                'reload',
            );
            cachePortraitBlob(originalSrc, blob);
            return blob;
        }
    }

    function untilAborted(task, signal) {
        if (!signal) return task;
        if (signal.aborted) return Promise.reject(namedError('AbortError', 'Portrait render was cancelled'));
        return new Promise((resolve, reject) => {
            const onAbort = () => reject(namedError('AbortError', 'Portrait render was cancelled'));
            signal.addEventListener('abort', onAbort, { once: true });
            task.then((value) => {
                signal.removeEventListener('abort', onAbort);
                resolve(value);
            }, (error) => {
                signal.removeEventListener('abort', onAbort);
                reject(error);
            });
        });
    }

    /* ---- decoded portraits -------------------------------------------------

       Keyed by the portrait's own source URL. An entry is the <img> that
       decoded it and, for a remote portrait, the object URL it paints from,
       which the store now owns (a session's release never revokes it). Bounded
       LRU; an entry whose image is on screen is never evicted. */
    function releaseDecodedPortrait(entry) {
        if (!entry?.objectUrl) return;
        try {
            URL.revokeObjectURL(entry.objectUrl);
        } catch (_) {
            // A revoked or browser-owned URL needs no further cleanup.
        }
    }

    function keepDecodedPortrait(source, image, objectUrl = null) {
        if (!source || !image) return;
        const existing = decodedPortraits.get(source);
        if (existing && existing.image !== image) {
            if (existing.image.isConnected) {
                // The one on screen stays the kept one; this copy is surplus.
                if (objectUrl && !activePortraitObjectUrls.has(objectUrl)) {
                    releaseDecodedPortrait({ objectUrl });
                }
                return;
            }
            releaseDecodedPortrait(existing);
        }
        if (objectUrl) activePortraitObjectUrls.delete(objectUrl);
        decodedPortraits.delete(source);
        decodedPortraits.set(source, { image, objectUrl });
        for (const [key, entry] of decodedPortraits) {
            if (decodedPortraits.size <= DECODED_PORTRAIT_MAX_ENTRIES) break;
            if (entry.image.isConnected) continue;
            decodedPortraits.delete(key);
            releaseDecodedPortrait(entry);
        }
    }

    /**
     * Put a decoded portrait where the grid's fresh placeholder stands. The
     * placeholder's attributes (alt, identity, classes) are this render's
     * truth; the image keeps the source it decoded from.
     */
    function adoptDecodedPortrait(placeholder) {
        const source = placeholder?.dataset?.originalSrc;
        const entry = source ? decodedPortraits.get(source) : null;
        if (!entry || entry.image.isConnected) return false;
        decodedPortraits.delete(source);
        decodedPortraits.set(source, entry);
        const { image } = entry;
        Array.from(placeholder.attributes).forEach(({ name, value }) => {
            if (name !== 'src') image.setAttribute(name, value);
        });
        image.dataset.portraitSettled = 'true';
        placeholder.replaceWith(image);
        return true;
    }

    function portraitSourcesOf(page) {
        return (page?.characters || []).map((char) => char.imageUrl).filter(Boolean);
    }

    /* A page is ready to present when every portrait with a real source is
       decoded and free to adopt. One the warm just failed to get is not waited
       on: it takes the grid's own neutral, retrying well. */
    function pagePortraitsReady(page) {
        return portraitSourcesOf(page).every((source) => {
            const entry = decodedPortraits.get(source);
            if (entry) return !entry.image.isConnected;
            const failedAt = portraitWarmFailures.get(source);
            return failedAt !== undefined && Date.now() - failedAt < PORTRAIT_FAILURE_GRACE_MS;
        });
    }

    /* The warm's attempts at a portrait: the first from the cache, the second
       past it, each on the ladder. None starts while another surface covers
       the lobby; one that yielded to a surface is asked for again once it
       closes, as the same attempt (nothing failed). */
    async function warmPortraitBytes(source) {
        let attempt = 0;
        for (;;) {
            if (coveredByAnotherSurface()) await LobbyCover.whenUncovered({ except: SURFACE_OWNER });
            try {
                return await fetchPortraitBlob(
                    source,
                    imageAttemptDeadlineMs(attempt),
                    null,
                    attempt === 0 ? 'default' : 'reload',
                    { yieldToCover: true },
                );
            } catch (error) {
                if (error?.name === 'YieldError') continue;
                attempt += 1;
                if (attempt >= CHARACTER_PORTRAIT_ATTEMPTS) throw error;
            }
        }
    }

    function warmPortraitBlob(source) {
        const cached = readCachedPortraitBlob(source);
        if (cached) return Promise.resolve(cached);
        const pending = portraitBlobWarms.get(source);
        if (pending) return pending;
        const request = warmPortraitBytes(source)
            .then((blob) => {
                cachePortraitBlob(source, blob);
                return blob;
            })
            .finally(() => {
                portraitBlobWarms.delete(source);
            });
        portraitBlobWarms.set(source, request);
        return request;
    }

    function warmPortrait(source) {
        if (decodedPortraits.has(source)) return Promise.resolve(true);
        const pending = portraitWarms.get(source);
        if (pending) return pending;
        const task = (async () => {
            const image = document.createElement('img');
            image.decoding = 'async';
            let objectUrl = null;
            try {
                let paintSrc = source;
                if (isRemotePortraitUrl(source)) {
                    const blob = await warmPortraitBlob(source);
                    objectUrl = URL.createObjectURL(blob);
                    paintSrc = objectUrl;
                }
                image.dataset.portraitSource = 'warm';
                image.setAttribute('src', paintSrc);
                await decodeCharacterPortrait(image, CHARACTER_PORTRAIT_LOCAL_LOAD_TIMEOUT_MS);
                keepDecodedPortrait(source, image, objectUrl);
                portraitWarmFailures.delete(source);
                return true;
            } catch (_) {
                if (objectUrl) {
                    // Bytes that cannot make pixels are never replayed.
                    deleteCachedPortraitBlob(source);
                    releaseDecodedPortrait({ objectUrl });
                }
                portraitWarmFailures.set(source, Date.now());
                return false;
            } finally {
                portraitWarms.delete(source);
            }
        })();
        portraitWarms.set(source, task);
        return task;
    }

    function warmPortraits(sources) {
        /* A kept portrait is decoded again on every warm: an engine may drop
           the decoded pixels of a picture that sits off screen (Firefox's
           surface cache expires them), and decoding at intent — moments before
           the press — keeps the dialog's first frame from painting without
           them. Cheap when the pixels are still there. */
        sources.forEach((source) => {
            const kept = decodedPortraits.get(source);
            if (kept && !kept.image.isConnected && typeof kept.image.decode === 'function') {
                Promise.resolve().then(() => kept.image.decode()).catch(() => {});
            }
        });
        const queue = sources.filter((source) => !decodedPortraits.has(source));
        let next = 0;
        const worker = async () => {
            while (next < queue.length) {
                const source = queue[next];
                next += 1;
                await warmPortrait(source);
            }
        };
        return Promise.all(Array.from(
            { length: Math.min(CHARACTER_PORTRAIT_FETCH_CONCURRENCY, queue.length) },
            worker,
        ));
    }

    /* The faces the dialog is set in. The lobby already uses them, so this is
       normally a no-op; it keeps a weight the lobby never drew from arriving
       as a late font swap on the dialog's first frame. */
    function warmSelectorFonts() {
        try {
            const fonts = document.fonts;
            if (!fonts || typeof fonts.load !== 'function') return;
            ['600 1em Cinzel', '700 1em Cinzel', '600 1em Spectral'].forEach((font) => {
                Promise.resolve(fonts.load(font)).catch(() => {});
            });
        } catch (_) {
            // A font warm never breaks the lobby.
        }
    }

    /**
     * Warm the selector ahead of the press: the roster (the account's
     * summoners, whole), the first page's portraits fetched and decoded, the
     * dialog's faces; then, behind it, the rest of the roster
     * (warmRosterAhead). Idempotent and memoised; resolves true once the first
     * page is ready to be presented whole (false if it could not be read —
     * the press then loads it as before). Called by the lobby after the boot
     * veil lifts and on every intent to start a battle; intent after the
     * roster's fresh window reads it again behind the view.
     */
    function warm() {
        const entry = SummonerRoster.read();
        warmSelectorFonts();
        void SummonerRoster.revalidate();
        return entry.promise
            .then((roster) => warmPortraits(portraitSourcesOf(rosterPage(roster, 1))).then(() => roster))
            .then((roster) => {
                warmRosterAhead(roster);
                return true;
            }, () => false);
    }

    /* ---- the rest of the roster, behind the first page ---------------------

       Everything a player can reach from the lobby's warm without another
       read: the second page decoded (a Next swaps it in at once), every
       summoner's decks (a choice anywhere opens its decks at once), and every
       other portrait of the roster fetched as bytes only, one at a time, in
       the one scheduler's background (a later page decodes from the cache).
       Bounded by the roster, which is bounded by the route's cap for one
       read; once per roster answer, however often intent asks. */
    let rosterWarmedFor = null;
    let rosterBytesScope = null;
    const ROSTER_BYTES_SCREENS = /** @type {const} */ (['boot', 'lobby', 'deck-selector', 'collection', 'store', 'foundry']);

    function warmRosterAhead(roster) {
        if (!roster || rosterWarmedFor === roster) return;
        rosterWarmedFor = roster;
        const ahead = [];
        for (let pageNumber = 2; pageNumber <= DECODED_AHEAD_PAGES; pageNumber += 1) {
            const page = rosterPage(roster, pageNumber);
            if (page) ahead.push(...portraitSourcesOf(page));
        }
        const decodedAhead = warmPortraits(ahead);
        warmRosterDecks(roster);
        void decodedAhead.then(() => {
            if (rosterWarmedFor === roster) warmRosterBytes(roster);
        });
    }

    function warmRosterDecks(roster) {
        const queue = normalizeCharacters(Array.isArray(roster?.rows) ? roster.rows : []);
        let next = 0;
        const worker = async () => {
            while (next < queue.length && rosterWarmedFor === roster) {
                const character = queue[next];
                next += 1;
                await warmCharacterDecks(character).promise;
            }
        };
        for (let index = 0; index < Math.min(DECK_WARM_CONCURRENCY, queue.length); index += 1) {
            void worker();
        }
    }

    function warmRosterBytes(roster) {
        const scheduler = typeof LoadScheduler?.scope === 'function' ? LoadScheduler : null;
        if (!scheduler) return;
        const rows = Array.isArray(roster?.rows) ? roster.rows : [];
        const sources = rows
            .slice(DECODED_AHEAD_PAGES * CHARACTER_PAGE_SIZE)
            .map((row) => sanitizeImageUrlForDirectClient(row?.imageUrl))
            .filter((source) => isRemotePortraitUrl(source) && !decodedPortraits.has(source));
        if (sources.length === 0) return;
        rosterBytesScope?.close();
        rosterBytesScope = scheduler.scope('summoner-roster', {
            runsOn: ROSTER_BYTES_SCREENS,
            // One portrait at a time: never both of the background's slots.
            maxInFlight: 1,
            // Not under Save-Data or on a link the browser rates slow.
            admit: (policy) => !policy?.constrained,
        });
        // For the lobby's press: none starts while another surface covers the
        // lobby, however long it stays open (the player's own screen).
        rosterBytesScope.waitWhile?.('another lobby surface', coveredByAnotherSurface, Infinity);
        void rosterBytesScope.soon(sources);
    }

    /* ---- the roster changed ------------------------------------------------

       A summoner made or deleted on this device drops the roster (the next
       warm or open reads it again); a re-read behind the view that found
       another list replaces it. A grid on screen shows the new page in one
       step once its portraits are in (capped, like a turn); any other state
       only forgets the pages it built from the old list. */
    function onRosterChanged(next) {
        rosterWarmedFor = null;
        rosterBytesScope?.close();
        rosterBytesScope = null;
        if (!isOpen) return;
        const keptPage = preparedPages.get(characterPage);
        preparedPages = new Map();
        if (keptPage) preparedPages.set(characterPage, keptPage);
        if (!next) return;
        const content = document.getElementById('ds-content-area');
        const onRosterPage = isRosterPage(characterPage, characterPage === 1 ? null : characterPageCursors.get(characterPage));
        if (!content || !onRosterPage) return;
        const lastPage = Math.max(1, Math.ceil((next.rows?.length || 0) / CHARACTER_PAGE_SIZE));
        const pageNumber = Math.min(characterPage, lastPage);
        const page = rosterPage(next, pageNumber);
        if (!page) return;
        const generation = sessionGeneration;
        const samePage = pageNumber === characterPage
            && page.characters.length === charactersData.length
            && page.characters.every((char, index) => {
                const shown = charactersData[index];
                return shown && shown.id === char.id && shown.name === char.name
                    && shown.description === char.description && shown.imageUrl === char.imageUrl;
            });
        const rebuildPagination = () => {
            characterPageCursors = new Map([[1, null]]);
            for (let number = 2; number <= pageNumber; number += 1) characterPageCursors.set(number, ROSTER_PAGE);
            preparedPages = new Map();
            applyCharacterPage(pageNumber, page);
        };
        if (samePage || content.dataset.stage !== 'characters' || content.dataset.reveal === 'held') {
            // Nothing on screen changes: the pager learns where the list ends now.
            rebuildPagination();
            if (content.dataset.stage === 'characters') updateCharacterPaginationControls();
            return;
        }
        void settleWithinCap(warmPortraits(portraitSourcesOf(page)), STAGE_REVEAL_CAP_MS).then(() => {
            if (!isCurrentSession(generation) || SummonerRoster.peek() !== next || pageTurn) return;
            if (content.dataset.stage !== 'characters' || content.dataset.reveal === 'held') return;
            const focused = document.activeElement?.closest?.('.ds-character-card')?.getAttribute('data-char-id') || null;
            rebuildPagination();
            renderCharacterSelection(generation, focused);
        });
    }

    SummonerRoster.onChange((next) => onRosterChanged(next));

    /* A deck made, changed or deleted (convex-client.js): every list kept is
       dropped, and read again by the next warm or the grid. */
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('moonfall:decks-changed', () => {
            deckReads = new Map();
            rosterWarmedFor = null;
        });
    }

    async function decodeCharacterPortraitWithRecovery(image, renderSignal) {
        const originalSrc = image.dataset.originalSrc;

        if (!originalSrc) {
            image.removeAttribute('src');
            image.removeAttribute('data-fallback-src');
            image.dataset.portraitSource = 'retrying-identity';
            image.classList.remove('ds-char-image-unavailable', 'ds-char-image-fallback');
            image.classList.add('ds-char-image-retrying');
            const identity = image.dataset.characterId || '';
            if (!identity) return 'retrying';

            const record = {
                signal: renderSignal,
                timerId: null,
                abortHandler: null,
                started: false,
            };
            const ownsResolution = () => (
                identityPortraitRecords.get(image) === record
                && !renderSignal?.aborted
                && image.isConnected
            );
            const beginResolution = () => {
                record.timerId = null;
                if (!ownsResolution() || record.started) return;
                const resolveCardArt = Cards?.resolveCardArt;
                if (typeof resolveCardArt !== 'function') {
                    record.timerId = window.setTimeout(beginResolution, 1000);
                    return;
                }
                record.started = true;
                let readiness;
                try {
                    readiness = resolveCardArt(
                        {
                            id: identity,
                            imageUrl: undefined,
                            picture: image.dataset.characterPicture || undefined,
                        },
                        image,
                        { directArtOnly: true, rasterReserveScale: 1, fit: 'contain' },
                    );
                } catch (_) {
                    record.started = false;
                    record.timerId = window.setTimeout(beginResolution, 1000);
                    return;
                }
                void Promise.resolve(readiness).then((ready) => {
                    if (!ownsResolution() || ready !== true) return;
                    image.dataset.portraitSource = 'direct-identity';
                    image.dataset.portraitSettled = 'true';
                    image.classList.remove(
                        'ds-char-image-retrying',
                        'ds-char-image-unavailable',
                        'ds-char-image-fallback',
                    );
                    updateCharacterPortraitGridState(image);
                }, () => {
                    if (!ownsResolution()) return;
                    record.started = false;
                    record.timerId = window.setTimeout(beginResolution, 1000);
                });
            };
            record.abortHandler = () => {
                if (identityPortraitRecords.get(image) !== record) return;
                if (record.timerId !== null) window.clearTimeout(record.timerId);
                identityPortraitRecords.delete(image);
                Cards?.disposeCardArtImage?.(image);
            };
            renderSignal?.addEventListener('abort', record.abortHandler, { once: true });
            identityPortraitRecords.set(image, record);
            beginResolution();
            return 'retrying';
        }

        try {
            let paintSrc = originalSrc;
            if (isRemotePortraitUrl(originalSrc)) {
                const blob = await fetchPortraitBlobWithRetry(image, originalSrc, renderSignal);
                if (renderSignal?.aborted || !image.isConnected) {
                    throw namedError('AbortError', 'Portrait render was cancelled');
                }
                paintSrc = URL.createObjectURL(blob);
                activePortraitObjectUrls.add(paintSrc);
                image.dataset.portraitObjectUrl = paintSrc;
                image.dataset.portraitSource = 'blob';
            } else {
                image.dataset.portraitSource = 'local';
            }

            image.setAttribute('src', paintSrc);
            await decodeCharacterPortrait(image, CHARACTER_PORTRAIT_LOCAL_LOAD_TIMEOUT_MS, renderSignal);
            return 'loaded';
        } catch (error) {
            if (renderSignal?.aborted || !image.isConnected || error?.name === 'AbortError') {
                releasePortraitObjectUrl(image);
                throw error;
            }
            // A successful HTTP response is not paint authority. If the Blob
            // cannot produce intrinsic pixels, evict those bytes immediately
            // so reopening the selector never replays the same corrupt cache
            // entry before trying the real remote image again.
            if (image.dataset.portraitSource === 'blob') {
                deleteCachedPortraitBlob(originalSrc);
                releasePortraitObjectUrl(image);
            }
        }
        scheduleCharacterPortraitRetry(image, renderSignal);
        return 'retrying';
    }

    async function settleCharacterPortraitPage(
        grid,
        generation,
        renderGeneration,
        preferredCharacterId,
        renderSignal,
    ) {
        // Adopted portraits arrived decoded; only the rest are fetched here.
        const portraits = Array.from(grid.querySelectorAll('.ds-char-image:not([data-portrait-settled="true"])'));
        try {
            // Convex storage occasionally starves one of several simultaneous
            // browser requests to the same host (seen in Chromium, while the
            // identical URL remains healthy). A two-worker pool avoids the
            // four-way burst without making four healthy response times add
            // up serially.
            // Real-time reveal (the owner's decision of 2026-07-24, superseding
            // a batched experiment that kept every well empty until the slowest
            // portrait landed, up to 2.5 s): each portrait is marked settled the
            // moment IT settles, never held back for the others. On a first
            // open that happens behind the stage's one loading state (capped
            // at STAGE_REVEAL_CAP_MS) — normally never, because the warm has
            // decoded the page before the press — and past the cap a straggler
            // shows the instant its pixels exist, exactly as before.
            let nextPortraitIndex = 0;
            const hydrateNextPortrait = async () => {
                while (nextPortraitIndex < portraits.length) {
                    const image = portraits[nextPortraitIndex];
                    nextPortraitIndex += 1;
                    const outcome = await decodeCharacterPortraitWithRecovery(image, renderSignal);
                    if (outcome === 'loaded') {
                        image.dataset.portraitSettled = 'true';
                        keepDecodedPortrait(
                            image.dataset.originalSrc,
                            image,
                            image.dataset.portraitObjectUrl || null,
                        );
                    }
                }
            };
            await Promise.all(
                Array.from(
                    { length: Math.min(CHARACTER_PORTRAIT_FETCH_CONCURRENCY, portraits.length) },
                    hydrateNextPortrait,
                ),
            );
        } catch (error) {
            if (
                isCurrentSession(generation)
                && renderGeneration === portraitRenderGeneration
                && grid.isConnected
            ) {
                grid.dataset.portraitState = 'failed';
                grid.setAttribute('aria-busy', 'false');
                releasePortraitObjectUrls();
                grid.innerHTML = `<div class="ds-error">${htmlText('deckSelector.failedLoadCharacters', 'Failed to load characters. Please refresh.')}</div>`;
                console.error('[DeckSelector] Failed to decode character portraits:', error);
            }
            return false;
        }

        if (
            !isCurrentSession(generation)
            || renderGeneration !== portraitRenderGeneration
            || !grid.isConnected
        ) {
            return false;
        }

        grid.dataset.portraitState = grid.querySelector('.ds-char-image-retrying')
            ? 'retrying'
            : 'ready';
        grid.setAttribute('aria-busy', 'false');
        /* Only a focus that has fallen out of the dialog is brought back. The
           grid is interactive before its art lands (docs/asset-loading.md law
           2), so by the time the last portrait decodes a player may be on a
           switch, the pager or the × — and taking the keyboard back to a
           champion card lost their place. A slow decode (WebKit, a phone on
           a slow link) made that the usual order, not a race. */
        focusCharacterChoiceUnlessMoved(preferredCharacterId);
        return true;
    }

    /* ---- the stage header, reconciled ---------------------------------------

       ONE header for both stages. Choosing a summoner used to rebuild the whole
       content area from a template, so the title and subtitle were torn down
       and drawn again: measured as a rebuild blink on both, 488 ms apart. Now
       the same `.ds-header`, `.ds-title` and `.ds-subtitle` elements carry
       both stages and only their words change; Back is added for the deck
       stage and removed for the champions. Its box is the same on both stages
       (deck-selector.css), so the change moves nothing. */
    function reconcileStageHeader(container, { title, subtitle, back }) {
        let header = container.querySelector(':scope > .ds-header');
        if (!header) {
            header = document.createElement('div');
            header.className = 'ds-header';
            const text = document.createElement('div');
            text.className = 'ds-header-text';
            const heading = document.createElement('h2');
            heading.className = 'ds-title';
            const line = document.createElement('p');
            line.className = 'ds-subtitle';
            text.append(heading, line);
            header.appendChild(text);
            container.prepend(header);
        }
        const heading = header.querySelector('.ds-title');
        if (heading.textContent !== title) heading.textContent = title;
        const line = header.querySelector('.ds-subtitle');
        if (line.textContent !== subtitle) line.textContent = subtitle;
        let backButton = header.querySelector(':scope > .ds-back-btn');
        if (back && !backButton) {
            backButton = document.createElement('button');
            backButton.className = 'ds-back-btn';
            backButton.id = 'ds-back-btn';
            backButton.type = 'button';
            backButton.textContent = uiText('deckSelector.back', '← Back');
            backButton.addEventListener('click', returnToCharacters);
            header.prepend(backButton);
        } else if (!back && backButton) {
            backButton.remove();
        }
        return header;
    }

    function returnToCharacters() {
        if (!isOpen) return;
        const priorCharacterId = selectedCharacter?.id || null;
        resetDeckPaginationState();
        renderCharacterSelection(sessionGeneration, priorCharacterId);
    }

    /* Replace a stage's body and keep what carries across: the reconciled
       header, the battle-preferences row, and a held stage's loading state. */
    function replaceStageBody(container, header, markup) {
        Array.from(container.children).forEach((child) => {
            if (child === header || child === battlePreferencesEl) return;
            if (child.classList.contains('ds-loading')) return;
            child.remove();
        });
        header.insertAdjacentHTML('afterend', markup);
    }

    /**
     * Stage 1: Render Character Selection Grid
     */
    function renderCharacterSelection(generation = sessionGeneration, preferredCharacterId = null) {
        if (!isCurrentSession(generation)) return Promise.resolve(false);
        const container = document.getElementById('ds-content-area');
        if (!container) return Promise.resolve(false);
        container.dataset.stage = 'characters';
        delete container.dataset.deckPagination;
        const renderGeneration = ++portraitRenderGeneration;
        clearPortraitRender();
        pendingFocusCharacterId = preferredCharacterId;
        // A new grid supersedes a summoner or a page still being read.
        openingChoice = null;
        pageTurn = null;

        if (charactersData.length === 0) {
            container.innerHTML = `<div class="ds-error">${htmlText('deckSelector.failedLoadCharacters', 'Failed to load characters. Please refresh.')}</div>`;
            return Promise.resolve(false);
        }

        portraitRenderAbortController = new AbortController();
        const renderSignal = portraitRenderAbortController.signal;

        const header = reconcileStageHeader(container, {
            title: uiText('deckSelector.chooseChampion', 'Choose Your Champion'),
            subtitle: uiText('deckSelector.chooseChampionSubtitle', 'Select a character to view their decks'),
            back: false,
        });
        replaceStageBody(container, header, `
            <div class="ds-character-grid" data-card-count="${charactersData.length}" data-portrait-state="loading" aria-busy="true">
                ${charactersData.map(char => {
            const isExcluded = excludedCharacter === char.id;
            const imagePath = getCharacterPortraitUrl(char);
            const safeImagePath = escapeHtml(imagePath);
            const charName = escapeHtml(char.name || char.id);
            const charDesc = escapeHtml(char.description || '');
            const charStatus = isExcluded
                ? `<span class="ds-char-status">${htmlText('deckSelector.takenByOpponent', 'Taken by Opponent')}</span>`
                : '';

            return `
                        <button class="ds-character-card db-character-card ${char.theme} ${isExcluded ? 'disabled' : ''}"
                             type="button"
                             data-char-id="${escapeHtml(char.id)}"
                             aria-disabled="${isExcluded ? 'true' : 'false'}"
                            ${isExcluded ? 'disabled' : ''}>
                            <span class="ds-char-image-wrapper db-char-image-wrapper">
                                <img data-original-src="${safeImagePath}" data-character-id="${escapeHtml(char.characterId || char.id)}" data-character-picture="${escapeHtml(char.picture || '')}" alt="${charName}" class="ds-char-image db-char-image" loading="eager" decoding="async" fetchpriority="high">
                            </span>
                            <span class="ds-char-info db-char-info">
                                <span class="ds-char-name db-char-name">${charName}</span>
                                <span class="ds-char-desc db-char-desc">${charDesc}</span>
                                ${charStatus}
                            </span>
                        </button>
                    `;
        }).join('')}
            </div>
            <div class="deck-builder-pagination ds-character-pagination" id="ds-character-pagination">
                <button class="btn deck-btn" id="ds-character-prev" type="button">${htmlText('common.prev', 'Prev')}</button>
                <div class="deck-builder-page-info" id="ds-character-page-info">${htmlText('common.pageOne', 'Page 1')}</div>
                <button class="btn deck-btn" id="ds-character-next" type="button">${htmlText('common.next', 'Next')}</button>
            </div>
        `);
        container.querySelectorAll('.ds-character-grid .ds-char-image').forEach(adoptDecodedPortrait);

        // Add Event Listeners
        updateCharacterPaginationControls();

        const prevBtn = document.getElementById('ds-character-prev');
        const nextBtn = document.getElementById('ds-character-next');
        /* A press during a turn counts from the page the turn is heading to
           (turnCharacterPage: fast hands). */
        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                const from = turnTarget();
                if (from <= 1) return;
                void turnCharacterPage(from - 1, prevBtn, generation);
            });
        }
        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                if (targetIsLastPage()) return;
                void turnCharacterPage(turnTarget() + 1, nextBtn, generation);
            });
        }

        // Every card, not `:not(:disabled)`: a disabled button fires no click,
        // and a card can become disabled AFTER this render when the opponent
        // locks that summoner mid-pick (see setExcludedCharacter).
        container.querySelectorAll('.ds-character-card').forEach(card => {
            card.addEventListener('click', () => {
                const charId = card.getAttribute('data-char-id');
                void selectCharacter(charId, generation);
            });
            /* Intent: read this summoner's decks before the press lands. */
            const warmDecks = () => {
                if (!isCurrentSession(generation)) return;
                const character = charactersData.find((char) => char.id === card.getAttribute('data-char-id'));
                if (character) warmCharacterDecks(character);
            };
            card.addEventListener('pointerenter', warmDecks);
            card.addEventListener('focus', warmDecks);
            card.addEventListener('touchstart', warmDecks, { passive: true });
        });
        const grid = container.querySelector('.ds-character-grid');
        const settled = settleCharacterPortraitPage(
            grid,
            generation,
            renderGeneration,
            preferredCharacterId,
            renderSignal,
        );
        focusCharacterChoiceUnlessMoved(preferredCharacterId);
        /* Once the page's pictures are in, read the decks of the summoners on
           it and the next page, so either press lands on a finished screen. */
        void settled.then((ready) => {
            if (ready) scheduleGridWarms(generation, renderGeneration);
        });
        return settled;
    }

    /**
     * Stage 2: Render Deck List for Selected Character
     *
     * The grid used to be wiped to a "Loading" spinner here and the deck
     * stage drawn ~490 ms later. The decks of the summoners on screen are read
     * while the grid is open, so this normally opens the stage in the press's
     * own task; a press that beats its read keeps the grid and answers on the
     * chosen card itself (`is-opening`), then the stage arrives whole.
     */
    async function selectCharacter(charId, generation = sessionGeneration) {
        if (!isOpen) return;
        const character = charactersData.find(c => c.id === charId);
        if (!character) return;
        selectedCharacter = character;
        resetDeckPaginationState();

        // Every press supersedes a choice still waiting on its decks.
        const choice = {};
        openingChoice = choice;
        const reading = warmCharacterDecks(character);
        if (!reading.settled) {
            markOpeningCharacter(charId);
            await reading.promise;
            // Another summoner, a page turn or a closed picker supersedes it.
            if (openingChoice !== choice || !isCurrentSession(generation)) return;
            markOpeningCharacter(null);
            // Taken by the opponent while its decks were being read.
            if (excludedCharacter === charId) {
                openingChoice = null;
                return;
            }
        }
        openingChoice = null;
        if (reading.ok) {
            applyDecksPage(1, reading.result);
        } else {
            console.error('[DeckSelector] Failed to load decks:', reading.error);
            applyDecksFailure();
        }
        renderSelectedCharacterDecks(generation);
    }

    function markOpeningCharacter(charId) {
        document.querySelectorAll('.ds-character-card.is-opening').forEach((card) => {
            card.classList.remove('is-opening');
            card.removeAttribute('aria-busy');
        });
        if (!charId) return;
        const card = Array.from(document.querySelectorAll('.ds-character-card'))
            .find((candidate) => candidate.getAttribute('data-char-id') === charId);
        if (!card) return;
        card.classList.add('is-opening');
        card.setAttribute('aria-busy', 'true');
    }

    /**
     * The opponent locked a summoner while this picker is open. The server
     * pushes the exclusion through `rematchState`, so the open grid reflects it
     * at once instead of waiting for the player to be refused on confirm. The
     * cards are patched in place — a re-render would restart every portrait
     * load — and a player already on the taken summoner's deck stage is sent
     * back to the grid. The server still refuses a taken summoner regardless;
     * this only spares the player the bounce.
     */
    function setExcludedCharacter(characterId) {
        const next = characterId || null;
        if (next === excludedCharacter) return;
        excludedCharacter = next;
        if (!isOpen) return;
        const container = document.getElementById('ds-content-area');
        if (!container) return;

        if (container.dataset.stage === 'decks') {
            if (selectedCharacter && excludedCharacter === selectedCharacter.id) {
                const priorCharacterId = selectedCharacter.id;
                resetDeckPaginationState();
                renderCharacterSelection(sessionGeneration, priorCharacterId);
            }
            return;
        }

        container.querySelectorAll('.ds-character-card').forEach((/** @type {HTMLButtonElement} */ card) => {
            const isExcluded = excludedCharacter === card.getAttribute('data-char-id');
            card.disabled = isExcluded;
            card.classList.toggle('disabled', isExcluded);
            card.setAttribute('aria-disabled', isExcluded ? 'true' : 'false');
            const status = card.querySelector('.ds-char-status');
            if (isExcluded && !status) {
                const label = document.createElement('span');
                label.className = 'ds-char-status';
                label.textContent = uiText('deckSelector.takenByOpponent', 'Taken by Opponent');
                card.querySelector('.ds-char-info')?.appendChild(label);
            } else if (!isExcluded && status) {
                status.remove();
            }
        });
    }

    function renderSelectedCharacterDecks(generation = sessionGeneration, preferredFocusId = 'ds-back-btn') {
        if (!isCurrentSession(generation) || !selectedCharacter) return;

        // Hide decks that wouldn't pass the match-entry gate (wrong size).
        // The user repairs them via the Deck Builder; they don't belong in the
        // launch dropdown as a clickable option.
        const REQUIRED_DECK_SIZE = 15;
        // Historic decks stored their character as either the deck key ("Orion")
        // or the raw characterId ("orionNightwhisper"); match both so a deck saved
        // under either form still shows under its champion.
        const belongsToCharacter = (d) =>
            d.character === selectedCharacter.id
            || (selectedCharacter.characterId && d.character === selectedCharacter.characterId);
        const deckCardCount = (d) => (Number.isFinite(d.cardCount)
            ? d.cardCount
            : (Array.isArray(d.cardIds) ? d.cardIds.length : 0));

        /* AN UNDER-SIZE DECK IS SHOWN, DISABLED — it is no longer DELETED from
           the list.

           This used to `return count >= REQUIRED_DECK_SIZE`, so a deck the
           player had built and saved simply was not there. Reported as *"my
           summoners have empty decks ... not empty, non existant, no choice
           other than random"* — and the database agreed with the player, not
           the picker: four of the reporting account's seven decks hold 12, 12,
           14 and 14 cards, so four of them vanished from their own launch
           screen with nothing said.

           Hiding was a deliberate choice ("the user repairs them via the Deck
           Builder"), but silence is what made it a bug: a deck that is missing
           and a deck that is one card short look identical, and only one of
           them has an obvious fix. It is listed with its count now, greyed and
           unclickable, which is the same information the Deck Builder would
           have given them — without the trip. */
        const charDecks = cachedDecks
            .filter(belongsToCharacter)
            .map((deck) => ({ deck, cards: deckCardCount(deck) }))
            .map((entry) => ({ ...entry, incomplete: entry.cards < REQUIRED_DECK_SIZE }));
        const container = document.getElementById('ds-content-area');
        if (container.dataset.stage !== 'decks') {
            // Leaving the grid: its portrait work and page turn end with it.
            portraitRenderGeneration += 1;
            clearPortraitRender();
            markOpeningCharacter(null);
            pageTurn = null;
        }
        /* MARK THE STAGE BEFORE ANYTHING MEASURES IT. The page size below asks
           whether this stage carries the battle-switch row, and the answer is
           this attribute — so setting it further down (where it used to live,
           beside `deckPagination`) meant the very first read still said
           "characters" and reserved 50px the deck stage no longer spends. The
           room came back and the decks did not. */
        container.dataset.stage = 'decks';
        const phonePagination = usesPhoneDeckPages();
        const allChoices = [
            /* ONE SHAPE FOR BOTH KINDS. The random entry used to be a bare
               `{ kind }`, which made this array a union the moment deck entries
               grew `incomplete`/`cards` — and every read of those below became
               a type error. Same fields, nulled, costs nothing at runtime. */
            ...(phonePagination && deckPage !== 1
                ? []
                : [{ kind: 'random', deck: null, incomplete: false, cards: 0 }]),
            ...charDecks.map((entry) => ({
                kind: 'deck', deck: entry.deck, incomplete: entry.incomplete, cards: entry.cards,
            })),
        ];
        /* Read ONCE per render. Reading it again further down could return a
           different number mid-render — the switch row can still be settling —
           and the slice, the "has next" test and the pager would then disagree
           about how big a page is. */
        const phonePageSize = phonePagination ? phoneDeckChoicePageSize() : 0;
        const sliceStart = phonePagination
            ? deckLocalSlice * phonePageSize
            : 0;
        const visibleChoices = phonePagination
            ? allChoices.slice(sliceStart, sliceStart + phonePageSize)
            : allChoices;
        const hasLocalNext = phonePagination
            && sliceStart + phonePageSize < allChoices.length;
        const canGoBack = phonePagination
            ? deckLogicalPage > 1
            : deckPage > 1;
        const canGoForward = hasLocalNext || !deckPageIsDone;
        const visiblePage = phonePagination ? deckLogicalPage : deckPage;
        container.dataset.deckPagination = phonePagination ? 'phone' : 'server';

        const header = reconcileStageHeader(container, {
            title: selectedCharacter.name || selectedCharacter.id,
            subtitle: uiText('deckSelector.selectDeckSubtitle', 'Select a deck to enter battle'),
            back: true,
        });
        replaceStageBody(container, header, `
            ${/* A FAILED STARTER CATALOGUE IS NOT THE PLAYER'S PROBLEM.
                  d59953b3 made it loud on both sides at once: the query throws and
                  the selector printed "Starter decks are unavailable right now."
                  with a Try again button. Its reasoning was sound for the operator
                  half and wrong for this one - a player who cannot be handed
                  starter decks also cannot do anything about it, and the banner
                  offers a retry for a condition no retry clears.
                  The operator half is kept and is where it belongs: the Convex
                  query still throws, `convex/decks.ts` still logs the reason, and
                  the console.error above still fires. This surface simply shows
                  the decks it does have.
                  `deckLoadFailed` is deliberately still loud. That is the
                  player's OWN decks failing to load, and going quiet there would
                  read as "your decks are gone", which is alarming and wrong. */''}
            ${deckLoadFailed ? `<div class="ds-deck-load-error" role="alert">
                <span>${htmlText('deckSelector.loadFailed', 'Could not load your decks.')}</span>
                <button class="btn deck-btn" id="ds-deck-retry" type="button">${htmlText('deckSelector.retry', 'Try again')}</button>
            </div>` : ''}

            <div class="ds-deck-list-wrapper">
                <div class="ds-deck-list">
                    ${visibleChoices.length > 0 ? visibleChoices.map((choice) => choice.kind === 'random' ? `
                        <button class="ds-deck-item random-deck-item" id="ds-random-item" type="button">
                            <span class="ds-deck-info">
                                <span class="ds-deck-name">🎲 ${htmlText('deckSelector.randomDeck', 'Random Deck')}</span>
                            </span>
                            <span class="ds-deck-select-arrow">${htmlText('deckSelector.select', 'Select →')}</span>
                        </button>
                    ` : `
                        <button class="ds-deck-item ${escapeHtml(selectedCharacter.theme)}${choice.incomplete ? ' ds-deck-item--incomplete' : ''}" type="button" data-deck-id="${escapeHtml(choice.deck.id)}"${choice.incomplete ? ' disabled aria-disabled="true"' : ''}>
                            <span class="ds-deck-info">
                                <span class="ds-deck-name">${deckNameHtml(choice.deck)}</span>
                                ${choice.deck.isOfficialStarter ? `<span class="ds-official-starter-badge">${htmlText('deckSelector.officialStarter', 'Starter')}</span>` : ''}
                            </span>
                            <span class="ds-deck-select-arrow">${choice.incomplete
                                ? `<span class="ds-deck-incomplete">${htmlText('deckSelector.incompleteDeck', `${choice.cards}/${REQUIRED_DECK_SIZE} cards`, { count: choice.cards, required: REQUIRED_DECK_SIZE })}</span>`
                                : htmlText('deckSelector.select', 'Select →')}</span>
                        </button>
                    `).join('') : `<div class="ds-no-decks">${htmlText('deckSelector.noSavedDecks', 'No saved decks. Create one!')}</div>`}
                </div>
            </div>
            <div class="deck-builder-pagination ds-character-pagination" id="ds-deck-pagination">
                <button class="btn deck-btn" id="ds-deck-prev" type="button">${htmlText('common.prev', 'Prev')}</button>
                <div class="deck-builder-page-info" id="ds-deck-page-info">${htmlText('common.page', `Page ${visiblePage}`, { page: visiblePage })}</div>
                <button class="btn deck-btn" id="ds-deck-next" type="button">${htmlText('common.next', 'Next')}</button>
            </div>
        `);

        // Bind Events (Back is the reconciled header's own).
        document.getElementById('ds-deck-retry')?.addEventListener('click', async () => {
            const reloaded = await loadDecksPage(1, { force: true, generation });
            if (!isCurrentSession(generation)) return;
            renderSelectedCharacterDecks(generation, reloaded ? 'ds-back-btn' : 'ds-deck-retry');
        });
        const deckPagination = document.getElementById('ds-deck-pagination');
        const previousDeckPage = document.getElementById('ds-deck-prev');
        const nextDeckPage = document.getElementById('ds-deck-next');
        deckPagination.style.display = pagerIsUseful({
            page: visiblePage,
            isDone: !canGoForward,
        })
            ? 'flex' : 'none';
        previousDeckPage.disabled = !canGoBack;
        nextDeckPage.disabled = !canGoForward;
        previousDeckPage.addEventListener('click', async () => {
            if (!canGoBack) return;
            if (phonePagination && deckLocalSlice > 0) {
                deckLocalSlice -= 1;
                deckLogicalPage -= 1;
                renderSelectedCharacterDecks(generation, 'ds-deck-prev');
                return;
            }
            const loaded = await loadDecksPage(deckPage - 1, { generation });
            if (!loaded) return;
            if (phonePagination) {
                /* SAME MEMBERSHIP AS THE RENDERER, or the phone's slice maths
                   count a different list than the one on screen. This dropped
                   under-size decks while the renderer now shows them disabled,
                   which would have put the page boundaries in the wrong place
                   for exactly the players who have such decks. */
                const priorChoices = [
                    ...(deckPage === 1 ? [{ kind: 'random' }] : []),
                    ...cachedDecks.filter(belongsToCharacter).map((deck) => ({ kind: 'deck', deck })),
                ];
                deckLocalSlice = Math.max(
                    0,
                    Math.ceil(priorChoices.length / phonePageSize) - 1,
                );
                deckLogicalPage = Math.max(1, deckLogicalPage - 1);
            }
            renderSelectedCharacterDecks(generation, 'ds-deck-prev');
        });
        nextDeckPage.addEventListener('click', async () => {
            if (!canGoForward) return;
            if (hasLocalNext) {
                deckLocalSlice += 1;
                deckLogicalPage += 1;
                renderSelectedCharacterDecks(generation, 'ds-deck-next');
                return;
            }
            const loaded = await loadDecksPage(deckPage + 1, { generation });
            if (!loaded) return;
            if (phonePagination) {
                deckLocalSlice = 0;
                deckLogicalPage += 1;
            }
            renderSelectedCharacterDecks(generation, 'ds-deck-next');
        });

        // Random Deck Click
        document.getElementById('ds-random-item')?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!selectedCharacter) {
                console.error('[DeckSelector] Random deck selected without a character');
                return;
            }
            handleDeckSelection({ character: selectedCharacter.id, cardIds: null });
        });

        container.querySelectorAll('.ds-deck-item').forEach(item => {
            const deckId = item.getAttribute('data-deck-id');
            if (!deckId) return;
            item.addEventListener('click', () => {
                const deck = cachedDecks.find(d => d.id === deckId);
                if (!deck) {
                    console.warn('[DeckSelector] Selected deck not found:', deckId);
                    return;
                }
                handleDeckSelection(deck);
            });
        });
        focusWithoutScroll(document.getElementById(preferredFocusId));
    }

    /**
     * Handle Final Deck Selection
     */
    function handleDeckSelection(deck) {
        // deck can be a lightweight config for random selection (character only)
        const includeManga = mangaPreferenceControl?.value?.();
        const lucid = lucidPreferenceControl?.value?.();
        close({
            outcome: {
                kind: 'selected',
                deck,
                ...(typeof includeManga === 'boolean' ? { includeManga } : {}),
                ...(typeof lucid === 'boolean' ? { lucid } : {}),
            },
            restoreFocus: false,
        });
    }

    function escapeHtml(text) {
        return String(text ?? '').replace(/[&<>"']/g, (char) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        }[char]));
    }

    return {
        choose,
        open,
        close,
        warm,
        setExcludedCharacter,
        isOpen: () => isOpen,
        pagerIsUseful,
        // Blob-lifetime seam: revoking a URL an <img> still points at is
        // irreversible, so the ordering rule needs its own coverage.
        __portraits: {
            track: (url) => activePortraitObjectUrls.add(url),
            releaseAll: releasePortraitObjectUrls,
        },
    };

})();

export { DeckSelector };
